import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PATH_BY_SCREEN,
  screenForPath,
  isAuthedPath,
  isLegalPath,
  scanIdForPath,
  scanPath,
  opportunityIdForPath,
  opportunityPath,
  pathMatchesScreen,
} from "./Router.jsx";

/* Main app — state, routing, top-level shell. */

const AUTH_KEY = "cn_auth_v1";
const POST_LOGIN_ACTION_KEY = "cn_post_login_action";
const THEME_KEY = "cn_theme";
const LOGOUT_PENDING_KEY = "cn_logout_pending";
const POST_LOGIN_FINISH_ONBOARDING = "finish-onboarding";
// Legacy persisted-state key — used to hold screen/onboardStep/opps but
// caused login bugs (a stale onboardStep="welcome" survived OAuth and put
// authed users back on the welcome screen). Kept here only so hardLogout
// can purge any leftover value from existing installs. The module-load
// cleanup below evicts the key on first boot of the new build.
const LEGACY_STATE_KEY = "cn_app_state_v2";
try { localStorage.removeItem(LEGACY_STATE_KEY); } catch (e) {}

function loadTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark") return v;
    // No explicit choice → follow the OS preference.
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } catch (e) { return "light"; }
}
function saveTheme(t) {
  try { localStorage.setItem(THEME_KEY, t); } catch (e) {}
}

function readLogoutPending() {
  try { return localStorage.getItem(LOGOUT_PENDING_KEY) === "1"; } catch (e) { return false; }
}

function writeLogoutPending(pending) {
  try {
    if (pending) localStorage.setItem(LOGOUT_PENDING_KEY, "1");
    else localStorage.removeItem(LOGOUT_PENDING_KEY);
  } catch (e) {}
}

// "/" doubles as the landing page for logged-out users AND the dashboard
// for logged-in users — that lets a freshly-signed-in user land on the
// dashboard without a redirect blip. Every authed screen still has a
// dedicated path under PATH_BY_SCREEN so deep links + browser refresh work.
function screenFromLocation() {
  if (typeof window === "undefined") return null;
  if (window.location.pathname === "/") return "dashboard";
  return screenForPath(window.location.pathname);
}
function pathForScreen(screen, isAuthed) {
  if (screen === "dashboard") return isAuthed ? "/dashboard" : "/";
  return PATH_BY_SCREEN[screen] || "/";
}

function loadAuth() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}
function saveAuth(a) {
  try {
    if (a) localStorage.setItem(AUTH_KEY, JSON.stringify(a));
    else localStorage.removeItem(AUTH_KEY);
  } catch (e) {}
}

function loadPostLoginAction() {
  try {
    return localStorage.getItem(POST_LOGIN_ACTION_KEY) || null;
  } catch (e) { return null; }
}

function savePostLoginAction(action) {
  try {
    if (action) localStorage.setItem(POST_LOGIN_ACTION_KEY, action);
    else localStorage.removeItem(POST_LOGIN_ACTION_KEY);
  } catch (e) {}
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "density": "medium",
  "scoreMode": "both",
  "sort": "best",
  "threshold": 0,
  "highOnly": false,
  "activeProfileId": ""
}/*EDITMODE-END*/;

const EMPTY_PROFILE = {
  id: "",
  color: "linear-gradient(135deg, #475569, #334155)",
  faviconUrl: "",
  initials: "+",
  name: "No business profile",
  website: "",
  description: "",
  customers: "",
  useCases: [],
  painPoints: [],
  competitors: [],
  keywords: [],
  avoid: [],
  subreddits: [],
  lastScan: "—",
};

function isoHoursAgo(hours) {
  return new Date(Date.now() - hours * 3_600_000).toISOString();
}

const DEMO_PROFILE_RECORD = {
  id: "demo-signalleaf",
  product_name: "SignalLeaf",
  website: "signalleaf.ai",
  short_description:
    "AI voice-of-customer briefings for SaaS teams. SignalLeaf turns interviews, support tickets, Reddit threads, and sales calls into crisp weekly GTM insights.",
  target_customers:
    "Seed to Series B SaaS teams, product marketers, customer success leads, and founder-led growth teams who need customer signal without a full research team.",
  use_cases:
    "Weekly insight brief, launch messaging input, churn signal digest, competitor mention roundup, voice-of-customer reporting",
  pain_points:
    "Scattered feedback, slow customer research, support insights trapped in tools, hard-to-prioritize requests, unclear messaging",
  competitors:
    "Dovetail, Grain, Productboard, Gong",
  positive_keywords:
    "customer insight, voice of customer, churn risk, support trends, interview notes, GTM research, competitive intel",
  negative_keywords:
    "crypto, gaming guild, meme page, politics",
  subreddits:
    "SaaS,startups,ProductMarketing,CustomerSuccess,Entrepreneur,smallbusiness,agency,ProductManagement",
  search_plan: {
    target_customers: [
      "Founder-led SaaS teams",
      "Product marketing managers",
      "Customer success leaders",
      "Revenue operations teams",
    ],
    jobs_to_be_done: [
      "Turn scattered feedback into a weekly summary",
      "Extract customer language for messaging and launches",
      "Spot churn or objection trends early",
      "Package market signal for leadership and GTM teams",
    ],
    pain_points: [
      "Customer signal is trapped across support, calls, and community threads",
      "Manual synthesis takes hours every week",
      "Teams debate anecdotes instead of patterns",
      "Messaging misses the language customers actually use",
    ],
    trigger_phrases: [
      "weekly customer brief",
      "voice of customer",
      "summarize support tickets",
      "churn signal",
      "competitor mentions",
      "customer language",
      "launch messaging",
    ],
    suggested_subreddits: [
      { name: "SaaS", size: "185k", reason: "Frequent workflow and tooling pain from B2B operators", signal: "high" },
      { name: "ProductMarketing", size: "42k", reason: "Strong fit for messaging and launch insight use cases", signal: "high" },
      { name: "CustomerSuccess", size: "29k", reason: "Churn, support, and renewal themes show up clearly here", signal: "high" },
      { name: "startups", size: "1.8m", reason: "Founders often ask about lightweight research and customer discovery", signal: "medium" },
      { name: "agency", size: "53k", reason: "Services teams need structured synthesis across many client conversations", signal: "medium" },
      { name: "ProductManagement", size: "156k", reason: "Good source for prioritization and roadmap evidence workflows", signal: "medium" },
    ],
    negative_matches: [
      "Pure social media scheduling",
      "Generic survey tooling",
      "Communities unrelated to B2B research or GTM",
    ],
    search_themes: [
      "weekly customer brief",
      "support-to-GTM handoff",
      "voice-of-customer reporting",
      "launch messaging evidence",
      "churn risk synthesis",
      "competitor mention roundup",
    ],
  },
  updated_at: isoHoursAgo(7),
};

const DEMO_OPPS = [
  {
    id: "demo-opp-01",
    opportunityId: "demo-opp-01",
    contentItemId: "demo-content-01",
    subreddit: "r/SaaS",
    kind: "post",
    title: "Founder wants a simple weekly digest of what customers keep asking for",
    redditTitle: "How are you all tracking repeated customer requests without building a giant spreadsheet?",
    excerpt:
      "We have feedback coming from support chats, onboarding calls, and random Slack threads. Every Friday I try to summarize it for the team, but it turns into a messy copy-paste ritual.",
    score: 94,
    tier: "high",
    intent: "Workflow pain",
    useCase: "Weekly insight brief",
    persona: "Founder-led SaaS team",
    pain: "Scattered feedback",
    why: "The buyer is already doing the exact manual synthesis workflow SignalLeaf automates, and they want a repeatable weekly output for the team.",
    angle: "Lead with the Friday-brief use case: one digest that groups repeated requests, top risks, and exact language customers used.",
    caution: "Keep the reply practical. They are asking for process help, not a broad AI pitch.",
    judged: true,
    confidence: "high",
    posted: "2h ago",
    postedAt: isoHoursAgo(2),
    comments: 38,
    upvotes: 126,
    permalink: "/r/SaaS/comments/demo01",
    status: null,
    firstSeenScanId: "demo-scan-20260524",
    latestScanId: "demo-scan-20260524",
    isNew: true,
    feedback: "relevant",
    threadContext: "Weekly synthesis of customer asks is still manual and brittle.",
  },
  {
    id: "demo-opp-02",
    opportunityId: "demo-opp-02",
    contentItemId: "demo-content-02",
    subreddit: "r/ProductMarketing",
    kind: "comment",
    title: "PMM is trying to turn support pain into sharper launch messaging",
    redditTitle: "Launch messaging keeps missing the actual objections users mention in tickets",
    excerpt:
      "Every feature launch recap sounds polished, but then support tells us users are still confused by the same three things. I wish there was a way to pull the real phrases into one place before we write the page copy.",
    score: 91,
    tier: "high",
    intent: "Messaging gap",
    useCase: "Launch messaging input",
    persona: "Product marketing manager",
    pain: "Support insights trapped in tools",
    why: "They explicitly want support-language distilled into reusable launch messaging, which maps cleanly to SignalLeaf's customer-language summaries.",
    angle: "Position SignalLeaf as a bridge between support reality and launch copy: collect recurring objections, label them, and turn them into message-ready bullets.",
    caution: "Acknowledge that they may already have ticketing analytics; differentiate on synthesis and phrasing, not dashboards.",
    judged: true,
    confidence: "high",
    posted: "4h ago",
    postedAt: isoHoursAgo(4),
    comments: 22,
    upvotes: 89,
    permalink: "/r/ProductMarketing/comments/demo02",
    status: null,
    firstSeenScanId: "demo-scan-20260524",
    latestScanId: "demo-scan-20260524",
    isNew: true,
    feedback: null,
    threadContext: "They need customer wording before the next launch page goes live.",
  },
  {
    id: "demo-opp-03",
    opportunityId: "demo-opp-03",
    contentItemId: "demo-content-03",
    subreddit: "r/CustomerSuccess",
    kind: "post",
    title: "CS lead wants an early-warning digest before renewals start slipping",
    redditTitle: "Anyone have a good way to spot churn signals before QBR season?",
    excerpt:
      "Our renewal risks show up as scattered complaints long before an account gets red-flagged. The hard part is spotting the themes across calls and support notes before leadership asks for a summary.",
    score: 89,
    tier: "high",
    intent: "Risk monitoring",
    useCase: "Churn signal digest",
    persona: "Customer success lead",
    pain: "Hard-to-prioritize requests",
    why: "The post describes recurring qualitative churn signals that need to be synthesized into a leadership-ready digest, which is a strong fit.",
    angle: "Anchor on 'early warning' and leadership visibility: summarize repeated friction themes, track rising complaints, and hand CS a weekly risk memo.",
    caution: "Don't promise predictive churn scoring if the product is primarily synthesis-oriented.",
    judged: true,
    confidence: "high",
    posted: "7h ago",
    postedAt: isoHoursAgo(7),
    comments: 17,
    upvotes: 64,
    permalink: "/r/CustomerSuccess/comments/demo03",
    status: null,
    firstSeenScanId: "demo-scan-20260524",
    latestScanId: "demo-scan-20260524",
    isNew: true,
    feedback: null,
    threadContext: "Renewal risk indicators are visible, but buried in messy qualitative notes.",
  },
  {
    id: "demo-opp-04",
    opportunityId: "demo-opp-04",
    contentItemId: "demo-content-04",
    subreddit: "r/startups",
    kind: "comment",
    title: "Solo founder is using Reddit threads as makeshift customer discovery",
    redditTitle: "Can reading niche communities actually replace formal user interviews early on?",
    excerpt:
      "I can't afford a full research stack yet, so I've been bookmarking Reddit posts, support forums, and review sites. The problem is I never revisit them in a structured way.",
    score: 86,
    tier: "high",
    intent: "Lightweight research",
    useCase: "Voice-of-customer reporting",
    persona: "Solo founder",
    pain: "Slow customer research",
    why: "They are actively piecing together market signal from public conversations and need structure more than raw volume.",
    angle: "Pitch SignalLeaf as the lightweight research memory: collect public signals, cluster themes, and turn them into a decision brief instead of a bookmark graveyard.",
    caution: "Frame it as an early-stage workflow upgrade, not enterprise research software.",
    judged: true,
    confidence: "high",
    posted: "9h ago",
    postedAt: isoHoursAgo(9),
    comments: 41,
    upvotes: 143,
    permalink: "/r/startups/comments/demo04",
    status: null,
    firstSeenScanId: "demo-scan-20260524",
    latestScanId: "demo-scan-20260524",
    isNew: true,
    feedback: null,
    threadContext: "They already trust community signal but have no system for reviewing it.",
  },
  {
    id: "demo-opp-05",
    opportunityId: "demo-opp-05",
    contentItemId: "demo-content-05",
    subreddit: "r/agency",
    kind: "post",
    title: "Agency owner is drowning in discovery notes across contractors",
    redditTitle: "How do you all keep client research consistent when multiple freelancers run calls?",
    excerpt:
      "Each strategist has their own notes style, so when we prep messaging recommendations for clients it takes forever to compare what people heard. I need one consistent synthesis layer.",
    score: 84,
    tier: "high",
    intent: "Ops cleanup",
    useCase: "Client research recap",
    persona: "Agency owner",
    pain: "Scattered feedback",
    why: "The core pain is standardizing messy qualitative notes across contributors, which is a strong services-side use case for the product.",
    angle: "Emphasize consistency: every call rolls into the same structure, which makes client readouts faster and protects insight quality as the team scales.",
    caution: "Keep the message service-friendly; agencies care about client delivery and internal consistency.",
    judged: true,
    confidence: "high",
    posted: "13h ago",
    postedAt: isoHoursAgo(13),
    comments: 29,
    upvotes: 58,
    permalink: "/r/agency/comments/demo05",
    status: null,
    firstSeenScanId: "demo-scan-20260524",
    latestScanId: "demo-scan-20260524",
    isNew: true,
    feedback: null,
    threadContext: "Multiple strategists are producing raw notes with no shared synthesis standard.",
  },
  {
    id: "demo-opp-06",
    opportunityId: "demo-opp-06",
    contentItemId: "demo-content-06",
    subreddit: "r/ProductManagement",
    kind: "post",
    title: "PM team wants a Friday competitor-mention roundup without manual combing",
    redditTitle: "Best way to summarize competitor mentions from calls and community chatter?",
    excerpt:
      "Every Friday I search Slack, Gong snippets, and a few public communities to see which competitors came up. It works, but only if I personally remember to do it.",
    score: 82,
    tier: "high",
    intent: "Competitive intel",
    useCase: "Competitor mention roundup",
    persona: "Product manager",
    pain: "Scattered feedback",
    why: "This is a textbook 'repeatable weekly synthesis' workflow, just focused on competitor mentions instead of general VOC.",
    angle: "Show how SignalLeaf can auto-cluster competitor mentions, objections, and switching language into a weekly summary for PM and sales.",
    caution: "Avoid sounding like a full CI suite; lead with summary and recurring themes.",
    judged: true,
    confidence: "high",
    posted: "15h ago",
    postedAt: isoHoursAgo(15),
    comments: 14,
    upvotes: 47,
    permalink: "/r/ProductManagement/comments/demo06",
    status: null,
    firstSeenScanId: "demo-scan-20260524",
    latestScanId: "demo-scan-20260524",
    isNew: true,
    feedback: null,
    threadContext: "Competitor monitoring exists, but only as a manual Friday ritual.",
  },
  {
    id: "demo-opp-07",
    opportunityId: "demo-opp-07",
    contentItemId: "demo-content-07",
    subreddit: "r/smallbusiness",
    kind: "post",
    title: "Small team needs customer pain grouped before the next roadmap meeting",
    redditTitle: "Anyone have a lightweight way to summarize customer complaints for leadership?",
    excerpt:
      "Between support inboxes and sales follow-ups, I know the themes are there, but the roadmap review still turns into anecdotes instead of a clean pattern readout.",
    score: 79,
    tier: "medium",
    intent: "Decision support",
    useCase: "Leadership readout",
    persona: "Ops manager",
    pain: "Hard-to-prioritize requests",
    why: "This is a strong 'pattern readout' problem. The team has enough signal but not enough structure to present it well.",
    angle: "Speak to decision clarity: cluster complaints, show frequency, and surface exact customer wording before roadmap debates.",
    caution: null,
    judged: true,
    confidence: "medium",
    posted: "1d ago",
    postedAt: isoHoursAgo(26),
    comments: 11,
    upvotes: 33,
    permalink: "/r/smallbusiness/comments/demo07",
    status: null,
    firstSeenScanId: "demo-scan-20260522",
    latestScanId: "demo-scan-20260524",
    isNew: false,
    feedback: null,
    threadContext: "They need leadership-ready summaries before planning meetings.",
  },
  {
    id: "demo-opp-08",
    opportunityId: "demo-opp-08",
    contentItemId: "demo-content-08",
    subreddit: "r/Entrepreneur",
    kind: "comment",
    title: "Operator wants support and sales language merged into one GTM view",
    redditTitle: "Sales says one thing, support says another, and product hears both too late",
    excerpt:
      "The issue isn't missing data. It's that every team sees a different slice, so nobody has a single summary of what customers are actually saying this month.",
    score: 77,
    tier: "medium",
    intent: "Cross-functional alignment",
    useCase: "Monthly GTM brief",
    persona: "Revenue operations lead",
    pain: "Support insights trapped in tools",
    why: "They are explicitly asking for synthesis across functions, which is a distinctive use case for a customer-signal briefing tool.",
    angle: "Lead with one source of truth for language trends across sales, support, and product, delivered as a concise monthly brief.",
    caution: null,
    judged: true,
    confidence: "medium",
    posted: "1d ago",
    postedAt: isoHoursAgo(31),
    comments: 19,
    upvotes: 52,
    permalink: "/r/Entrepreneur/comments/demo08",
    status: null,
    firstSeenScanId: "demo-scan-20260522",
    latestScanId: "demo-scan-20260524",
    isNew: false,
    feedback: null,
    threadContext: "Cross-team language drift is making GTM alignment harder.",
  },
  {
    id: "demo-opp-09",
    opportunityId: "demo-opp-09",
    contentItemId: "demo-content-09",
    subreddit: "r/SaaS",
    kind: "comment",
    title: "Support manager is tired of manually labeling ticket themes in Airtable",
    redditTitle: "We built a homegrown VOC tracker and now nobody wants to maintain it",
    excerpt:
      "The taxonomy was a great idea until real work got busy. Now we have hundreds of useful notes and no appetite to clean them into categories by hand.",
    score: 74,
    tier: "medium",
    intent: "Automation search",
    useCase: "Ticket theme clustering",
    persona: "Support manager",
    pain: "Scattered feedback",
    why: "The user has already built a manual workaround and is feeling the maintenance burden, which is strong buying energy.",
    angle: "Meet them where they are: auto-group the same underlying issues and produce a lightweight brief instead of another taxonomy project.",
    caution: "Avoid criticizing their existing process; position the product as a relief valve.",
    judged: true,
    confidence: "medium",
    posted: "2d ago",
    postedAt: isoHoursAgo(45),
    comments: 26,
    upvotes: 71,
    permalink: "/r/SaaS/comments/demo09",
    status: null,
    firstSeenScanId: "demo-scan-20260520",
    latestScanId: "demo-scan-20260524",
    isNew: false,
    feedback: "not_relevant",
    threadContext: "Their DIY taxonomy is collapsing under maintenance load.",
  },
  {
    id: "demo-opp-10",
    opportunityId: "demo-opp-10",
    contentItemId: "demo-content-10",
    subreddit: "r/startups",
    kind: "post",
    title: "Growth team wants customer-language snippets for the homepage refresh",
    redditTitle: "What's the fastest way to collect authentic phrasing for a homepage rewrite?",
    excerpt:
      "We have enough customer calls and community posts to know what resonates, but extracting the exact phrasing for a homepage pass is painfully slow.",
    score: 83,
    tier: "high",
    intent: "Messaging refresh",
    useCase: "Homepage messaging brief",
    persona: "Growth lead",
    pain: "Slow customer research",
    why: "They are seeking exact language extraction from existing qualitative data, a highly actionable and product-aligned need.",
    angle: "Offer a phrase-level messaging brief: top objections, repeated value language, and wording patterns worth testing on the homepage.",
    caution: null,
    judged: true,
    confidence: "high",
    posted: "3d ago",
    postedAt: isoHoursAgo(72),
    comments: 34,
    upvotes: 119,
    permalink: "/r/startups/comments/demo10",
    status: null,
    firstSeenScanId: "demo-scan-20260518",
    latestScanId: "demo-scan-20260524",
    isNew: false,
    feedback: null,
    threadContext: "They already have signal; they need usable phrasing fast.",
  },
  {
    id: "demo-opp-11",
    opportunityId: "demo-opp-11",
    contentItemId: "demo-content-11",
    subreddit: "r/CustomerSuccess",
    kind: "comment",
    title: "CS ops lead wants a board-friendly voice-of-customer summary every month",
    redditTitle: "How do you package customer sentiment for leadership without sharing 50 screenshots?",
    excerpt:
      "I can find examples, but packaging them into a clean monthly narrative is the part that drains time. The board wants trends, not a dump of quotes.",
    score: 80,
    tier: "medium",
    intent: "Executive reporting",
    useCase: "Monthly executive brief",
    persona: "CS operations lead",
    pain: "Unclear messaging",
    why: "The need is not more data collection. It's transforming customer sentiment into an executive-ready narrative, which is exactly the demo story we want.",
    angle: "Position SignalLeaf as the final-mile translator from raw customer quotes to a clean leadership narrative with trend lines and standout language.",
    caution: null,
    judged: true,
    confidence: "medium",
    posted: "4d ago",
    postedAt: isoHoursAgo(95),
    comments: 9,
    upvotes: 28,
    permalink: "/r/CustomerSuccess/comments/demo11",
    status: null,
    firstSeenScanId: "demo-scan-20260518",
    latestScanId: "demo-scan-20260524",
    isNew: false,
    feedback: null,
    threadContext: "They need a narrative, not a pile of screenshots.",
  },
];

const DEMO_SCANS = [
  {
    id: "demo-scan-20260524",
    started_at: isoHoursAgo(6),
    finished_at: isoHoursAgo(5.5),
    candidates_count: 146,
    surfaced_count: 6,
    status: "complete",
    themes: ["weekly customer brief", "support-to-GTM handoff", "churn signal digest", "competitor mention roundup"],
    error_message: null,
  },
  {
    id: "demo-scan-20260522",
    started_at: isoHoursAgo(30),
    finished_at: isoHoursAgo(29.4),
    candidates_count: 118,
    surfaced_count: 4,
    status: "complete",
    themes: ["roadmap evidence", "cross-team synthesis", "voice-of-customer ops"],
    error_message: null,
  },
  {
    id: "demo-scan-20260520",
    started_at: isoHoursAgo(52),
    finished_at: isoHoursAgo(51.6),
    candidates_count: 93,
    surfaced_count: 2,
    status: "partial",
    themes: ["ticket clustering", "pricing objection trends", "manual taxonomy cleanup"],
    error_message: "One subreddit returned partial data, but surfaced matches were still saved.",
  },
  {
    id: "demo-scan-20260518",
    started_at: isoHoursAgo(77),
    finished_at: isoHoursAgo(76.3),
    candidates_count: 171,
    surfaced_count: 5,
    status: "complete",
    themes: ["homepage messaging", "board-ready customer reporting", "launch research"],
    error_message: null,
  },
];

const DEMO_SCAN_RESULTS_BY_ID = {
  "demo-scan-20260524": DEMO_OPPS.filter((opp) => [
    "demo-opp-01",
    "demo-opp-02",
    "demo-opp-03",
    "demo-opp-04",
    "demo-opp-05",
    "demo-opp-06",
  ].includes(opp.id)),
  "demo-scan-20260522": DEMO_OPPS.filter((opp) => [
    "demo-opp-07",
    "demo-opp-08",
    "demo-opp-03",
    "demo-opp-04",
  ].includes(opp.id)),
  "demo-scan-20260520": DEMO_OPPS.filter((opp) => [
    "demo-opp-09",
    "demo-opp-05",
  ].includes(opp.id)),
  "demo-scan-20260518": DEMO_OPPS.filter((opp) => [
    "demo-opp-10",
    "demo-opp-11",
    "demo-opp-06",
    "demo-opp-08",
    "demo-opp-04",
  ].includes(opp.id)),
};

function clearBrowserStorage(options = {}) {
  const { preserveLogoutPending = false } = options;
  try {
    const theme = localStorage.getItem(THEME_KEY);
    const logoutPending = preserveLogoutPending ? localStorage.getItem(LOGOUT_PENDING_KEY) : null;
    localStorage.clear();
    if (theme === "light" || theme === "dark") {
      localStorage.setItem(THEME_KEY, theme);
    }
    if (preserveLogoutPending && logoutPending) {
      localStorage.setItem(LOGOUT_PENDING_KEY, logoutPending);
    }
  } catch (e) {}
  try { sessionStorage.clear(); } catch (e) {}
}

function clearBrowserCookies() {
  try {
    const cookies = document.cookie ? document.cookie.split(";") : [];
    const hostname = window.location.hostname || "";
    const hostParts = hostname.split(".").filter(Boolean);
    const domains = [""];
    for (let i = 0; i < hostParts.length; i++) {
      domains.push(`.${hostParts.slice(i).join(".")}`);
    }

    cookies.forEach((cookie) => {
      const eq = cookie.indexOf("=");
      const rawName = eq >= 0 ? cookie.slice(0, eq) : cookie;
      const name = rawName.trim();
      if (!name) return;

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; max-age=0; path=/`;
      domains.forEach((domain) => {
        if (!domain) return;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
        document.cookie = `${name}=; max-age=0; path=/; domain=${domain}`;
      });
    });
  } catch (e) {}
}

window.clearAppBrowserState = function clearAppBrowserState(options = {}) {
  clearBrowserStorage(options);
  clearBrowserCookies();
};

window.hardLogout = async function hardLogout(options = {}) {
  if (window.__cnHardLogoutPromise) return window.__cnHardLogoutPromise;
  const {
    skipClerkSignOut = false,
    waitForClerkMs = 10000,
    confirmSignedOutMs = 15000,
  } = options;

  window.__cnHardLoggingOut = true;
  writeLogoutPending(true);

  const waitForClerk = async () => {
    const start = Date.now();
    while (!window.Clerk && Date.now() - start < waitForClerkMs) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return !!window.Clerk;
  };

  const waitForSignedOut = async () => {
    const start = Date.now();
    while (Date.now() - start < confirmSignedOutMs) {
      if (!window.Clerk?.user && !window.Clerk?.session) return true;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return !window.Clerk?.user && !window.Clerk?.session;
  };

  const finalize = ({ preserveLogoutPending = false } = {}) => {
    if (window.__cnHardLogoutFinalized) return;
    window.__cnHardLogoutFinalized = true;
    window.clearAppBrowserState?.({ preserveLogoutPending });
    try { localStorage.removeItem(LEGACY_STATE_KEY); } catch (e) {}
    if (!preserveLogoutPending) writeLogoutPending(false);
    window.location.replace("/");
  };

  const promise = (async () => {
    let signedOut = true;
    if (!skipClerkSignOut) {
      try {
        const present = await waitForClerk();
        if (present) {
          if (!window.Clerk.loaded) {
            await window.Clerk.load();
          }
          if (window.Clerk?.user || window.Clerk?.session) {
            await Promise.resolve(window.Clerk.signOut()).catch(() => {});
          }
          signedOut = await waitForSignedOut();
        }
      } catch (e) {}
    }

    finalize({ preserveLogoutPending: !signedOut });
  })()
    .catch(() => {
      finalize({ preserveLogoutPending: true });
    })
    .finally(() => {
      window.__cnHardLogoutPromise = null;
    });

  window.__cnHardLogoutFinalized = false;
  window.__cnHardLogoutPromise = promise;
  return promise;
};

window.CurrentUserContext = window.CurrentUserContext || React.createContext({
  user: null,
  loading: false,
  refreshUser: async () => null,
  setUser: () => {},
});
window.useCurrentUser = function useCurrentUser() {
  return React.useContext(window.CurrentUserContext);
};

const PROFILE_COLOR_PALETTE = [
  "linear-gradient(135deg, #1d4ed8, #0f766e)",
  "linear-gradient(135deg, #7c3aed, #2563eb)",
  "linear-gradient(135deg, #c2410c, #dc2626)",
  "linear-gradient(135deg, #0f766e, #16a34a)",
  "linear-gradient(135deg, #be123c, #7c3aed)",
];

function initialsForName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "BP";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

function splitCsv(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatRelativeDay(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffHours = Math.max(0, (now.getTime() - d.getTime()) / 3_600_000);
  if (diffHours < 1) return "just now";
  if (diffHours < 24) return `${Math.round(diffHours)}h ago`;
  const diffDays = Math.round(diffHours / 24);
  return diffDays <= 1 ? "1d ago" : `${diffDays}d ago`;
}

function toUiProfile(serverProfile, index = 0) {
  if (!serverProfile) return null;
  return {
    id: serverProfile.id,
    color: PROFILE_COLOR_PALETTE[index % PROFILE_COLOR_PALETTE.length],
    faviconUrl: serverProfile.favicon_url || "",
    initials: initialsForName(serverProfile.product_name),
    name: serverProfile.product_name || "Untitled business",
    website: serverProfile.website || "No website added",
    description: serverProfile.short_description || "",
    customers: serverProfile.target_customers || "",
    useCases: splitCsv(serverProfile.use_cases),
    painPoints: splitCsv(serverProfile.pain_points),
    competitors: splitCsv(serverProfile.competitors),
    keywords: splitCsv(serverProfile.positive_keywords),
    avoid: splitCsv(serverProfile.negative_keywords),
    subreddits: splitCsv(serverProfile.subreddits),
    lastScan: formatRelativeDay(serverProfile.updated_at),
    _server: serverProfile,
  };
}

function useIsMobile(query = "(max-width: 720px)") {
  const [is, setIs] = React.useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIs(e.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [query]);
  return is;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [t, setT] = window.useTweaks(TWEAK_DEFAULTS);
  const isMobile = useIsMobile();

  // ────────────────────────────────────────────────────────
  // Auth (demo only — no real validation)
  // ────────────────────────────────────────────────────────
  const [auth, setAuth] = React.useState(() => loadAuth());
  const [currentUser, setCurrentUser] = React.useState(null);
  const [currentUserLoading, setCurrentUserLoading] = React.useState(false);
  const [businessProfiles, setBusinessProfiles] = React.useState([]);
  const [businessProfilesLoading, setBusinessProfilesLoading] = React.useState(false);
  const [businessProfilesHydrated, setBusinessProfilesHydrated] = React.useState(false);
  const [businessProfilesError, setBusinessProfilesError] = React.useState(null);
  const [profileDetailsById, setProfileDetailsById] = React.useState({});
  const profileDetailsByIdRef = React.useRef({});
  const [suppressCreateProfilePrompt, setSuppressCreateProfilePrompt] = React.useState(false);
  const [activeProfileId, setActiveProfileId] = React.useState(
    () => window.api?.storage?.readProfileId?.() || t.activeProfileId
  );
  const [showProfileCreator, setShowProfileCreator] = React.useState(false);
  const [showLogin, setShowLogin] = React.useState(false);
  const [pendingPostLoginAction, setPendingPostLoginAction] = React.useState(() => loadPostLoginAction());
  const [authBootstrapLoading, setAuthBootstrapLoading] = React.useState(() => readLogoutPending());
  const [authTransitionMode, setAuthTransitionMode] = React.useState(() => (readLogoutPending() ? "logout" : null));
  const [finalizingOnboarding, setFinalizingOnboarding] = React.useState(false);
  const isDemo = auth?.email === "demo@example.com";
  const clearOnboardingDraft = React.useCallback(() => {
    try { window.api?.storage?.clearPendingProfile?.(); } catch (e) {}
    try { window.api?.storage?.clearPendingPlan?.(); } catch (e) {}
    setPendingPostLoginAction(null);
    savePostLoginAction(null);
    setSearchPlan(null);
  }, []);
  // Drop the queued "finish onboarding" intent without touching the draft, so
  // backing out of the radar can't trigger a profile build on a later sign-in.
  const clearSignupIntent = React.useCallback(() => {
    setPendingPostLoginAction(null);
    savePostLoginAction(null);
  }, []);
  const refreshCurrentUser = React.useCallback(async (userIdOverride) => {
    if (isDemo) {
      const demoUser = { id: "demo-user", name: "Avery", email: "demo@example.com" };
      setCurrentUser(demoUser);
      setCurrentUserLoading(false);
      return demoUser;
    }
    const userId = userIdOverride || window.api?.auth?.readUserId?.();
    if (!userId || typeof window.getSupabaseUserById !== "function") {
      setCurrentUser(null);
      return null;
    }
    setCurrentUserLoading(true);
    try {
      const row = await window.getSupabaseUserById(userId);
      setCurrentUser(row || null);
      return row || null;
    } finally {
      setCurrentUserLoading(false);
    }
  }, [isDemo]);
  const refreshBusinessProfiles = React.useCallback(async (userIdOverride) => {
    if (isDemo) {
      setBusinessProfiles([DEMO_PROFILE_RECORD]);
      setBusinessProfilesLoading(false);
      setBusinessProfilesHydrated(true);
      setBusinessProfilesError(null);
      return [DEMO_PROFILE_RECORD];
    }
    const userId = userIdOverride || window.api?.auth?.readUserId?.();
    if (!userId || !window.api?.profile?.list) {
      setBusinessProfiles([]);
      setBusinessProfilesHydrated(false);
      setBusinessProfilesError(null);
      return [];
    }
    setBusinessProfilesLoading(true);
    setBusinessProfilesHydrated(false);
    setBusinessProfilesError(null);
    try {
      const data = await window.api.profile.list(userId);
      const rows = Array.isArray(data?.profiles) ? data.profiles : [];
      setBusinessProfiles(rows);
      setBusinessProfilesHydrated(true);
      return rows;
    } catch (e) {
      console.warn("Could not load business profiles:", e);
      setBusinessProfiles([]);
      setBusinessProfilesHydrated(true);
      setBusinessProfilesError(e);
      return [];
    } finally {
      setBusinessProfilesLoading(false);
    }
  }, [isDemo]);
  const refreshProfileDetail = React.useCallback(async (profileIdOverride, options = {}) => {
    const { force = false } = options;
    if (isDemo) return DEMO_PROFILE_RECORD;
    const profileId =
      profileIdOverride ||
      activeProfileIdRef.current ||
      window.api?.storage?.readProfileId?.();
    if (!profileId || !window.api?.profile?.get) return null;
    const cached = profileDetailsByIdRef.current[profileId];
    if (!force && cached) {
      return cached;
    }
    try {
      const resp = await window.api.profile.get(profileId);
      const next = resp?.profile || null;
      setProfileDetailsById((prev) => {
        if (!next) {
          if (!(profileId in prev)) return prev;
          const copy = { ...prev };
          delete copy[profileId];
          return copy;
        }
        return { ...prev, [profileId]: next };
      });
      return next;
    } catch (e) {
      console.warn("Could not load profile detail:", e);
      return null;
    }
  }, [isDemo]);
  React.useEffect(() => {
    profileDetailsByIdRef.current = profileDetailsById;
  }, [profileDetailsById]);
  // Single source of truth for "user just signed in". Every entry point
  // (LoginScreen widget, OAuth bootstrap effect, demo button) routes through
  // here, so the post-login UI transition can't get skipped — that gap is
  // what previously stranded users on the WelcomeScreen after Google OAuth.
  const onLogin = React.useCallback(async (payload) => {
    const login = typeof payload === "string" ? { email: payload } : (payload || {});
    const email = login.email || "you@example.com";
    // Only auto-build a business profile when a *new* user arrives straight
    // from the onboarding radar (they approved it, which queued the
    // finish-onboarding intent). An existing user signing in — even with a
    // stale draft left in localStorage — must never get a surprise profile.
    const shouldFinalizePendingProfile =
      pendingPostLoginAction === POST_LOGIN_FINISH_ONBOARDING &&
      login.isNewUser === true &&
      !!window.api?.storage?.readPendingProfile?.();
    const next = { email, at: Date.now() };
    saveAuth(next);
    setAuth(next);
    setAuthTransitionMode(null);
    if (login.user) {
      setCurrentUser(login.user);
      setCurrentUserLoading(false);
    }
    setShowLogin(false);
    if (shouldFinalizePendingProfile) {
      setFinalizingOnboarding(true);
      setShowOnboard(false);
      setOnboardStep("done");
      setShowProfileCreator(false);

      try {
        const created = await window.api?.profile?.flushPending?.();
        const createdProfileId = created?.business_profile_id || null;
        const createdProfile = created?.profile || null;
        const userId =
          login.user?.user_id ||
          window.api?.auth?.readUserId?.() ||
          null;

        if (!createdProfileId) {
          throw new Error("Pending onboarding did not create a business profile.");
        }

        try { window.api?.storage?.writeProfileId?.(createdProfileId); } catch (e) {}
        setActiveProfileId(createdProfileId);
        if (createdProfile) {
          setBusinessProfiles([createdProfile]);
          setBusinessProfilesHydrated(true);
          setBusinessProfilesError(null);
          setProfileDetailsById((prev) => ({
            ...prev,
            [createdProfileId]: createdProfile,
          }));
        }
        await refreshBusinessProfiles(userId);
        setSearchPlan(null);
        setPendingPostLoginAction(null);
        savePostLoginAction(null);
        if (location.pathname !== "/dashboard") {
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        console.warn("Could not finalize onboarding after login:", e);
        setShowOnboard(true);
        setOnboardStep("map");
      } finally {
        setFinalizingOnboarding(false);
      }
      return;
    }

    // Treat the logged-out onboarding shell as disposable draft state unless
    // the user explicitly approved it and is finishing onboarding via auth.
    clearOnboardingDraft();
    setShowOnboard(false);
    setOnboardStep("done");
    if (location.pathname !== "/dashboard") {
      navigate("/dashboard", { replace: true });
    }
  }, [clearOnboardingDraft, location.pathname, navigate, pendingPostLoginAction, refreshBusinessProfiles]);

  React.useEffect(() => {
    savePostLoginAction(pendingPostLoginAction);
  }, [pendingPostLoginAction]);

  // Turn a live Clerk session into an app login. Both the cold-start
  // bootstrap probe and the live Clerk listener (below) funnel through here,
  // and either may fire first depending on how quickly Clerk finishes the
  // OAuth/transfer handshake after the redirect. The session-id guard makes
  // sure we only ever complete a given session once.
  const clerkLoginHandledRef = React.useRef(null);
  const handleClerkSignedIn = React.useCallback(async (user, options = {}) => {
    if (!user || !window.Clerk?.session) return;
    const sessionId = window.Clerk.session.id || user.id;
    if (clerkLoginHandledRef.current === sessionId) return;
    clerkLoginHandledRef.current = sessionId;

    // Clear __clerk_* params from the URL so a refresh doesn't re-process the
    // callback. The screen-sync effect replaces it with the final path next.
    try {
      const nextUrl = new URL(window.location.href);
      let changed = false;
      Array.from(nextUrl.searchParams.keys()).forEach((key) => {
        if (key.startsWith("__clerk_")) {
          nextUrl.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) {
        window.history.replaceState(window.history.state, "", nextUrl.toString());
      }
    } catch (e) {}

    let supabaseUser = null;
    try {
      if (typeof window.upsertSupabaseUser === "function") {
        supabaseUser = await window.upsertSupabaseUser(user);
      }
    } catch (e) {
      console.warn("Supabase upsert error:", e);
    }

    const email =
      user.primaryEmailAddress?.emailAddress ||
      user.emailAddresses?.[0]?.emailAddress ||
      "you@example.com";

    onLogin({ email, user: supabaseUser, isNewUser: options.isNewUser === true });
  }, [onLogin]);

  React.useEffect(() => {
    if (!auth) {
      setCurrentUser(null);
      setCurrentUserLoading(false);
      return;
    }
    if (currentUser) return;
    refreshCurrentUser();
  }, [auth, currentUser, refreshCurrentUser]);

  const logoutInFlightRef = React.useRef(null);
  const onLogout = async () => {
    if (logoutInFlightRef.current) return logoutInFlightRef.current;

    const promise = (async () => {
      setAuthTransitionMode("logout");
      saveAuth(null);
      setAuth(null);
      setCurrentUser(null);
      setCurrentUserLoading(false);
      setBusinessProfiles([]);
      setBusinessProfilesHydrated(false);
      setBusinessProfilesError(null);
      setProfileDetailsById({});
      setSuppressCreateProfilePrompt(false);
      setShowLogin(false);
      setShowOnboard(false);
      setShowProfileCreator(false);
      setOnboardStep("welcome");
      setSearchPlan(null);
      setProfileOverrides({});
      if (location.pathname !== "/") navigate("/", { replace: true });
      await window.hardLogout?.();
    })().finally(() => {
      logoutInFlightRef.current = null;
    });

    logoutInFlightRef.current = promise;
    return promise;
  };

  // ────────────────────────────────────────────────────────
  // Screen — derived from the URL on mount, then kept in sync by the
  // location-watching effect below. The router owns the URL; we mirror
  // it into state so the render switch (which keys on screen names)
  // keeps working.
  // ────────────────────────────────────────────────────────
  const [screen, setScreen] = React.useState(
    screenFromLocation() || "dashboard"
  );
  const routeScanId = React.useMemo(
    () => scanIdForPath(location.pathname),
    [location.pathname]
  );
  const routeOpportunityId = React.useMemo(
    () => opportunityIdForPath(location.pathname),
    [location.pathname]
  );
  const backgroundScanId = React.useMemo(() => {
    if (!routeOpportunityId) return null;
    return location.state?.backgroundScanId || null;
  }, [routeOpportunityId, location.state]);
  const locationScreen = React.useMemo(() => {
    if (location.pathname === "/") return "dashboard";
    return screenForPath(location.pathname);
  }, [location.pathname]);

  const goToScreen = React.useCallback((nextScreen, options = {}) => {
    setScreen(nextScreen);
    const nextPath = pathForScreen(nextScreen, !!auth);
    if (location.pathname !== nextPath) {
      navigate(nextPath, {
        replace: !!options.replace,
        state: options.state,
      });
    }
  }, [auth, location.pathname, navigate]);

  // Two-way URL ↔ screen sync. React Router owns the URL; we mirror it into
  // the `screen` state so the existing render switch (which keys on screen
  // names) keeps working unchanged. The shell mounts the same component on
  // every route (see Router.jsx) so cross-screen state (active profile,
  // opportunities cache, scan dialog, toasts) survives navigation without a
  // remount.
  React.useEffect(() => {
    if (isLegalPath(location.pathname)) return;
    if (!auth && isAuthedPath(location.pathname)) {
      if (!authBootstrapLoading) navigate("/", { replace: true });
      return;
    }
    if (locationScreen && locationScreen !== screen) return;
    if (!pathMatchesScreen(screen, location.pathname, !!auth)) {
      navigate(pathForScreen(screen, !!auth), { replace: true });
    }
  }, [screen, auth, authBootstrapLoading, location.pathname, navigate, locationScreen]);

  const openScanRoute = React.useCallback((scanId) => {
    if (!scanId) return;
    setScreen("scans");
    const nextPath = scanPath(scanId);
    if (location.pathname !== nextPath) {
      navigate(nextPath);
    }
  }, [location.pathname, navigate]);

  const backToScansRoute = React.useCallback((options = {}) => {
    setScreen("scans");
    if (location.pathname !== "/scans") {
      navigate("/scans", { replace: !!options.replace });
    }
  }, [location.pathname, navigate]);

  const clearDetailState = React.useCallback(() => {
    setSelectedId(null);
    setExternalOpp(null);
    setDetailOpen(false);
  }, []);

  const closeDetail = React.useCallback((options = {}) => {
    const nextScreen = options.nextScreen || location.state?.backgroundScreen || "opportunities";
    if (routeOpportunityId) {
      clearDetailState();
      const nextPath = nextScreen === "scans" && backgroundScanId
        ? scanPath(backgroundScanId)
        : pathForScreen(nextScreen, !!auth);
      if (location.pathname !== nextPath) {
        navigate(nextPath, { replace: !!options.replace });
      }
      return;
    }
    clearDetailState();
  }, [auth, backgroundScanId, clearDetailState, location.pathname, location.state, navigate, routeOpportunityId]);

  const openOpp = React.useCallback((idOrOpp, genMode) => {
    const nextId = idOrOpp && typeof idOrOpp === "object" ? idOrOpp.id : idOrOpp;
    if (!nextId) return;
    if (idOrOpp && typeof idOrOpp === "object") {
      setExternalOpp(idOrOpp);
    } else {
      setExternalOpp(null);
    }
    // A string mode ("comment" | "pm") from a "Generate …" feed button opens
    // the detail panel straight into that composer; a plain open clears it.
    setPendingGenMode(typeof genMode === "string" ? genMode : null);
    setSelectedId(nextId);
    setDetailOpen(true);
    const nextPath = opportunityPath(nextId);
    if (location.pathname !== nextPath) {
      navigate(nextPath, {
        state: {
          backgroundScreen: screen,
          backgroundScanId: screen === "scans" ? routeScanId : null,
        },
      });
    }
  }, [location.pathname, navigate, routeScanId, screen]);

  React.useEffect(() => {
    if (isLegalPath(location.pathname)) return;
    if (location.pathname === "/") {
      if (auth) setScreen("dashboard");
      return;
    }
    if (routeOpportunityId && location.state?.backgroundScreen) {
      setScreen(location.state.backgroundScreen);
      return;
    }
    const next = screenForPath(location.pathname);
    if (next) setScreen(next);
  }, [location.pathname, auth, routeOpportunityId, location.state]);

  React.useEffect(() => {
    if (routeOpportunityId) {
      setSelectedId(routeOpportunityId);
      setDetailOpen(true);
      return;
    }
    setExternalOpp(null);
    setSelectedId(null);
    setDetailOpen(false);
  }, [routeOpportunityId]);
  // Onboarding step is purely in-memory. Pre-auth onboarding is now a
  // scratchpad, so we never carry a saved draft into the logged-in app.
  const [onboardStep, setOnboardStep] = React.useState("done");
  const [showOnboard, setShowOnboard] = React.useState(onboardStep !== "done");
  // Seed typed into the welcome prompt. Non-empty → DescribeProductScreen
  // auto-analyzes it and skips straight to the radar; null → guided flow.
  const [onboardSeed, setOnboardSeed] = React.useState(null);
  const startOnboarding = (rawValue) => {
    const value = typeof rawValue === "string" ? rawValue.trim() : "";
    setOnboardSeed(value || null);
    setShowOnboard(true);
    setOnboardStep("setup");
  };

  React.useEffect(() => {
    let cancelled = false;

    const waitForClerk = async () => {
      const start = Date.now();
      while (!window.Clerk && Date.now() - start < 10000) {
        await new Promise((r) => setTimeout(r, 50));
      }
      return !!window.Clerk;
    };

    const bootstrapClerkSession = async () => {
      const logoutPending = readLogoutPending();
      if (authTransitionMode === "logout" || logoutPending) {
        if (authTransitionMode !== "logout") {
          setAuthTransitionMode("logout");
        }
        setAuthBootstrapLoading(true);
        if (logoutPending && !window.__cnHardLogoutPromise) {
          window.hardLogout?.();
        }
        return;
      }
      if (auth) {
        setAuthBootstrapLoading(false);
        return;
      }
      if (isLegalPath(window.location.pathname)) {
        setAuthBootstrapLoading(false);
        return;
      }
      if (window.location.pathname === "/login") {
        setAuthBootstrapLoading(false);
        return;
      }

      const hasIntent =
        !!pendingPostLoginAction ||
        !!window.api?.storage?.readPendingProfile?.() ||
        !!window.api?.storage?.readPendingPlan?.();
      const landedOnAuthedPath = isAuthedPath(window.location.pathname);
      const hasClerkCallback = Array.from(new URLSearchParams(window.location.search).keys())
        .some((key) => key.startsWith("__clerk_"));
      const landedOnLandingPage = window.location.pathname === "/";
      const hasExistingClerkSession = !!window.Clerk?.session || !!window.Clerk?.user;
      const shouldShowBootstrapLoader =
        hasIntent || hasClerkCallback || landedOnAuthedPath || hasExistingClerkSession;
      const shouldProbeExistingSession =
        shouldShowBootstrapLoader || landedOnLandingPage;
      setAuthBootstrapLoading(shouldShowBootstrapLoader);
      if (authTransitionMode !== "logout") {
        setAuthTransitionMode(shouldShowBootstrapLoader ? "login" : null);
      }
      if (!shouldProbeExistingSession) {
        setAuthBootstrapLoading(false);
        if (authTransitionMode !== "logout") setAuthTransitionMode(null);
        return;
      }

      const present = await waitForClerk();
      if (!present || cancelled) {
        if (!cancelled) {
          setAuthBootstrapLoading(false);
          if (authTransitionMode !== "logout") setAuthTransitionMode(null);
        }
        return;
      }

      try {
        if (!window.Clerk.loaded) {
          await window.Clerk.load();
        }
      } catch (e) {
        console.warn("Could not initialize Clerk session bootstrap:", e);
        setAuthBootstrapLoading(false);
        if (authTransitionMode !== "logout") setAuthTransitionMode(null);
        return;
      }
      if (cancelled) return;

      // When OAuth comes back via redirect (custom Google/Apple buttons in
      // LoginScreen call signIn.authenticateWithRedirect), Clerk may have
      // landed in a "transferable" state instead of an active session —
      // either because the user has no account yet (sign-in → sign-up
      // transfer) or because they already do (sign-up → sign-in transfer).
      // The mounted <SignIn /> widget used to handle this for us; with
      // custom buttons we have to complete the handshake ourselves.
      let didCreateNewAccount = false;
      if (!window.Clerk.user || !window.Clerk.session) {
        const client = window.Clerk.client;
        const cSignIn = client?.signIn;
        const cSignUp = client?.signUp;
        try {
          if (cSignUp?.verifications?.externalAccount?.status === "transferable") {
            const res = await cSignIn.create({ transfer: true });
            if (res.status === "complete") {
              await window.Clerk.setActive({ session: res.createdSessionId });
            }
          } else if (cSignIn?.firstFactorVerification?.status === "transferable") {
            const res = await cSignUp.create({ transfer: true });
            if (res.status === "complete") {
              didCreateNewAccount = true;
              await window.Clerk.setActive({ session: res.createdSessionId });
            } else if (res.status === "missing_requirements") {
              const done = await cSignUp.update({});
              if (done.status === "complete") {
                didCreateNewAccount = true;
                await window.Clerk.setActive({ session: done.createdSessionId });
              }
            }
          }
        } catch (e) {
          console.warn("Clerk OAuth transfer failed:", e);
        }
        if (cancelled) return;
      }

      const user = window.Clerk.user;
      const session = window.Clerk.session;
      if (!user || !session) {
        setAuthBootstrapLoading(false);
        if (authTransitionMode !== "logout") setAuthTransitionMode(null);
        return;
      }

      if (cancelled) return;
      await handleClerkSignedIn(user, { isNewUser: didCreateNewAccount });
    };

    bootstrapClerkSession();
    return () => { cancelled = true; };
  }, [auth, authTransitionMode, pendingPostLoginAction, location.pathname]);

  // Safety net for the OAuth redirect race. Clerk's script auto-processes the
  // callback on load, and on a cold return its session can become active a
  // beat *after* the one-shot bootstrap probe above has already run and given
  // up — which used to strand users on the welcome screen until they manually
  // refreshed. A live listener completes the login the instant Clerk reports
  // an active session, so the dashboard loads immediately with no refresh.
  // Skipped while a finish-onboarding intent is pending: that flow needs the
  // bootstrap's isNewUser detection to flush the pending business profile.
  React.useEffect(() => {
    if (auth) return;
    if (authTransitionMode === "logout" || readLogoutPending()) return;
    if (pendingPostLoginAction === POST_LOGIN_FINISH_ONBOARDING) return;
    if (isLegalPath(location.pathname)) return;
    if (location.pathname === "/login") return;

    let cancelled = false;
    let removeListener = null;

    const attach = async () => {
      const start = Date.now();
      while (!window.Clerk && Date.now() - start < 10000) {
        await new Promise((r) => setTimeout(r, 50));
      }
      if (cancelled || !window.Clerk) return;

      const maybeComplete = () => {
        if (cancelled) return;
        if (window.__cnHardLoggingOut || readLogoutPending()) return;
        if (window.Clerk?.user && window.Clerk?.session) {
          handleClerkSignedIn(window.Clerk.user);
        }
      };

      try {
        removeListener = window.Clerk.addListener(() => maybeComplete());
      } catch (e) {}
      // Catch a session that already went active before we attached.
      maybeComplete();
    };

    attach();
    return () => {
      cancelled = true;
      try { removeListener && removeListener(); } catch (e) {}
    };
  }, [auth, authTransitionMode, pendingPostLoginAction, location.pathname, handleClerkSignedIn]);

  // Search plan generated by profile_expansion. Inside the logged-out
  // onboarding shell it is draft-only state and should not auto-create a
  // business profile on login.
  const [searchPlan, setSearchPlan] = React.useState(
    () => window.api?.storage?.readPendingPlan?.() || null
  );
  const onPlanGenerated = React.useCallback((plan) => {
    setSearchPlan(plan);
    try { window.api?.storage?.writePendingPlan?.(plan); } catch (e) {}
  }, []);

  React.useEffect(() => { setT({ activeProfileId }); }, [activeProfileId]);

  React.useEffect(() => {
    if (!auth) {
      setBusinessProfiles([]);
      setBusinessProfilesHydrated(false);
      setBusinessProfilesError(null);
      setProfileDetailsById({});
      setSuppressCreateProfilePrompt(false);
      return;
    }
    refreshBusinessProfiles();
  }, [auth, refreshBusinessProfiles]);

  const mustCreateBusinessProfile =
    !!auth &&
    !isDemo &&
    !showLogin &&
    !showOnboard &&
    !finalizingOnboarding &&
    businessProfilesHydrated &&
    !businessProfilesLoading &&
    !businessProfilesError &&
    !suppressCreateProfilePrompt &&
    businessProfiles.length === 0;

  React.useEffect(() => {
    if (!mustCreateBusinessProfile) return;
    if (location.pathname !== "/dashboard") {
      navigate("/dashboard", { replace: true });
    }
    setShowProfileCreator(true);
  }, [location.pathname, mustCreateBusinessProfile, navigate]);

  React.useEffect(() => {
    if (businessProfiles.length > 0 && suppressCreateProfilePrompt) {
      setSuppressCreateProfilePrompt(false);
    }
  }, [businessProfiles.length, suppressCreateProfilePrompt]);

  React.useEffect(() => {
    if (!isDemo) return;
    if (!activeProfileId) {
      setActiveProfileId(DEMO_PROFILE_RECORD.id);
    }
    try { window.api?.storage?.writeProfileId?.(DEMO_PROFILE_RECORD.id); } catch (e) {}
  }, [activeProfileId, isDemo]);

  const uiBusinessProfiles = React.useMemo(
    () => businessProfiles.map((serverProfile, index) => toUiProfile(serverProfile, index)),
    [businessProfiles]
  );

  const activeProfileIdRef = React.useRef(activeProfileId);
  React.useEffect(() => {
    activeProfileIdRef.current = activeProfileId;
  }, [activeProfileId]);

  React.useEffect(() => {
    if (uiBusinessProfiles.length === 0) return;
    const storedId = window.api?.storage?.readProfileId?.();
    const preferredId = activeProfileId || storedId;
    const exists = uiBusinessProfiles.some((profile) => profile.id === preferredId);
    const nextId = exists ? preferredId : uiBusinessProfiles[0].id;
    if (nextId && nextId !== activeProfileId) {
      setActiveProfileId(nextId);
    }
    if (nextId) {
      window.api?.storage?.writeProfileId?.(nextId);
    }
  }, [activeProfileId, uiBusinessProfiles]);

  React.useEffect(() => {
    if (!auth || isDemo || !businessProfilesHydrated || businessProfilesLoading) return;
    const storedId = window.api?.storage?.readProfileId?.();
    const preferredId = activeProfileId || storedId || businessProfiles[0]?.id || null;
    if (!preferredId) return;
    const shouldForce = preferredId === activeProfileId;
    refreshProfileDetail(preferredId, { force: shouldForce });
  }, [
    activeProfileId,
    auth,
    businessProfiles,
    businessProfilesHydrated,
    businessProfilesLoading,
    isDemo,
    refreshProfileDetail,
  ]);

  const baseProfile = uiBusinessProfiles.find((p) => p.id === activeProfileId) || uiBusinessProfiles[0] || EMPTY_PROFILE;
  const [profileOverrides, setProfileOverrides] = React.useState({});
  const profile = { ...baseProfile, ...(profileOverrides[baseProfile.id] || {}) };

  const [opps, setOpps] = React.useState([]);
  const [oppsLoading, setOppsLoading] = React.useState(false);
  // Timestamp of the last successful refresh — surfaces "Updated Xs ago" in
  // the UI and powers the periodic-refresh decision.
  const [oppsUpdatedAt, setOppsUpdatedAt] = React.useState(null);
  // Latest scan summary the opportunities response shipped with — used by
  // page headers and the dashboard stats.
  const [latestScan, setLatestScan] = React.useState(null);
  React.useEffect(() => {
    setOpps([]);
    setOppsUpdatedAt(null);
    setLatestScan(null);
  }, [activeProfileId]);
  const oppsRequestSeqRef = React.useRef(0);
  // Track which opp ids the user has already seen (for the "new" dot).
  // Persists across refreshes; cleared on profile switch.
  const seenOppIdsRef = React.useRef(new Set());
  React.useEffect(() => { seenOppIdsRef.current = new Set(); }, [activeProfileId]);

  // Stable ref into the latest opps list so refreshOpps can decide whether
  // to suppress its spinner without taking a re-render dependency on the
  // array itself.
  const oppsRef = React.useRef([]);

  // Pull real opportunities from the latest opportunity_scan. Always shows
  // ONLY LLM-judged rows — the backend filters non-judged out, and
  // mapResults drops anything that slipped through without a `judged` block.
  //
  // The spinner only fires when we don't already have data on screen, so
  // background refreshes never flash a loading state over results the user
  // is reading.
  const refreshOpps = React.useCallback(async (profileIdOverride, { silent = false } = {}) => {
    if (isDemo) {
      setOppsLoading(false);
      setOpps(DEMO_OPPS);
      setLatestScan(DEMO_SCANS[0]);
      setOppsUpdatedAt(Date.now());
      return;
    }
    const profileId =
      profileIdOverride ||
      activeProfileIdRef.current ||
      window.api?.storage?.readProfileId?.();
    const requestSeq = ++oppsRequestSeqRef.current;
    if (!profileId || !window.api?.opportunities?.latest) {
      if (requestSeq === oppsRequestSeqRef.current) {
        setOppsLoading(false);
        setOpps([]);
        setLatestScan(null);
      }
      return;
    }
    // `silent` skips the loading spinner — used by background refreshes
    // (scan-progress polling / Realtime bumps) so a periodic refetch never
    // flashes the skeleton over results or the live scanning view.
    const hasExisting = oppsRef.current && oppsRef.current.length > 0;
    if (!hasExisting && !silent) setOppsLoading(true);
    try {
      const data = await window.api.opportunities.latest(profileId);
      if (requestSeq !== oppsRequestSeqRef.current) return;
      const rows = Array.isArray(data?.results) ? data.results : [];
      const mapped = window.api.opportunities.mapResults
        ? window.api.opportunities.mapResults(rows)
        : rows.map(window.api.opportunities.mapResult).filter(Boolean);

      // Two-layer "new" rule:
      //   1. Backend marks `isNew` true iff the opportunity was first
      //      surfaced by the LATEST scan. This is the canonical signal —
      //      it survives refreshes, profile switches, and tab reloads.
      //   2. Local `seenOppIdsRef` overrides backend-new → false once the
      //      user has actually interacted with the card this session
      //      (clicked it, gave feedback). This keeps the "New" dot from
      //      flashing back on after the user has acknowledged the row.
      const seen = seenOppIdsRef.current;
      const ranked = mapped.map((o) => ({
        ...o,
        isNew: o.isNew === true && !seen.has(o.id),
      }));
      setOpps(ranked);
      setLatestScan(data?.scan || null);
      setOppsUpdatedAt(Date.now());
    } catch (e) {
      if (requestSeq !== oppsRequestSeqRef.current) return;
      console.warn("Could not load opportunities:", e);
      if (!hasExisting) setOpps([]);
    } finally {
      if (requestSeq === oppsRequestSeqRef.current && !hasExisting && !silent) {
        setOppsLoading(false);
      }
    }
  }, [isDemo]);
  React.useEffect(() => { oppsRef.current = opps; }, [opps]);

  React.useEffect(() => { refreshOpps(activeProfileId); }, [activeProfileId, refreshOpps]);

  // Steady state: no focus / visibility / interval refresh. The inbox is
  // durable (lives in the `opportunities` table cross-scan), so outside of a
  // scan it's refetched on exactly three triggers: initial load / profile
  // switch, after a scan completes, and the manual refresh button. The
  // scan-scoped polling fallback (active only while a scan runs) lives further
  // down, next to the scanning state it depends on.

  // Detail panel
  const [selectedId, setSelectedId] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  // Composer to auto-open with the detail panel ("comment" | "pm" | null),
  // set by the "Generate …" buttons on feed cards.
  const [pendingGenMode, setPendingGenMode] = React.useState(null);
  // Opp opened from a source outside the latest feed (e.g. a past scan's
  // surfaced list). Held here so the detail panel can render it even though
  // it isn't in `opps`.
  const [externalOpp, setExternalOpp] = React.useState(null);

  // Scanning state (manual scan)
  const [isScanning, setIsScanning] = React.useState(false);
  // Subreddits the in-flight scan actually targeted (the subset the user
  // selected in the scan dialog), so the live scanning view lists only those
  // — not every subreddit on the profile's radar. null until a scan starts
  // this session (e.g. after a reload mid-scan), in which case the scanning
  // view falls back to the full profile list.
  const [scanSubreddits, setScanSubreddits] = React.useState(null);
  // Bump to force the scans screen to re-fetch history after a run.
  const [scanRefreshKey, setScanRefreshKey] = React.useState(0);
  // Scan dialog — every "Scan now" button opens this instead of scanning
  // directly, so the user can pick a window / subreddits / themes first.
  const [scanDialogOpen, setScanDialogOpen] = React.useState(false);
  // Scan-history meta the dialog needs: whether a first scan has run yet,
  // plus the most recent scan's label + surfaced count for its subtitle.
  const [scanMeta, setScanMeta] = React.useState({
    profileId: null,
    loaded: false,
    hasComplete: false,
    lastLabel: null,
    lastSurfaced: null,
  });

  // Reliability net for the live scan experience. The Realtime push in
  // pollScanUntilComplete gives instant updates as each follow-up chunk
  // surfaces opportunities, but postgres_changes delivery is best-effort — a
  // dropped, coalesced, or late-subscribed event would otherwise leave new
  // cards invisible until a manual reload (the "sometimes I have to refresh"
  // problem). While a scan is running we poll every few seconds so the inbox
  // AND the scans history converge regardless of Realtime; it stops the moment
  // the scan finishes. `silent` keeps it from flashing the loading skeleton.
  React.useEffect(() => {
    if (isDemo || !isScanning) return undefined;
    const tick = () => {
      // Skip while the tab is backgrounded — no point hammering Supabase
      // (through Flask) when nobody's watching; it resumes on refocus.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      refreshOpps(undefined, { silent: true });
      setScanRefreshKey((k) => k + 1);
    };
    const id = setInterval(tick, 7000);
    return () => clearInterval(id);
  }, [isDemo, isScanning, refreshOpps]);

  // Toasts
  const [toasts, setToasts] = React.useState([]);
  const toastTimersRef = React.useRef(new Map());
  const clearToastTimers = React.useCallback((id) => {
    const timers = toastTimersRef.current.get(id);
    if (timers) {
      clearTimeout(timers.leaveTimer);
      clearTimeout(timers.removeTimer);
      toastTimersRef.current.delete(id);
    }
  }, []);
  const dismissToast = React.useCallback((id) => {
    clearToastTimers(id);
    setToasts((arr) => arr.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    const removeTimer = setTimeout(() => {
      setToasts((arr) => arr.filter((t) => t.id !== id));
      toastTimersRef.current.delete(id);
    }, 220);
    toastTimersRef.current.set(id, { leaveTimer: null, removeTimer });
  }, [clearToastTimers]);
  const scheduleToastDismiss = React.useCallback((id, duration = 2400) => {
    clearToastTimers(id);
    const leaveTimer = setTimeout(() => {
      setToasts((arr) => arr.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    }, duration);
    const removeTimer = setTimeout(() => {
      setToasts((arr) => arr.filter((t) => t.id !== id));
      toastTimersRef.current.delete(id);
    }, duration + 300);
    toastTimersRef.current.set(id, { leaveTimer, removeTimer });
  }, [clearToastTimers]);
  const pushToast = React.useCallback((text, kind = "success", options = {}) => {
    const id = options.id || Math.random().toString(36).slice(2);
    const sticky = !!options.sticky;
    setToasts((arr) => {
      const next = arr.filter((t) => t.id !== id);
      return [...next, { id, text, kind, sticky, leaving: false }];
    });
    if (sticky) clearToastTimers(id);
    else scheduleToastDismiss(id, options.duration ?? 2400);
    return id;
  }, [clearToastTimers, scheduleToastDismiss]);
  React.useEffect(() => () => {
    toastTimersRef.current.forEach((timers) => {
      clearTimeout(timers.leaveTimer);
      clearTimeout(timers.removeTimer);
    });
    toastTimersRef.current.clear();
  }, []);

  // Keep scan-history meta fresh for the Scan dialog. Re-fetches whenever the
  // active profile changes or a scan finishes (scanRefreshKey bump).
  React.useEffect(() => {
    let cancelled = false;
    if (isDemo) {
      setScanMeta({
        profileId: DEMO_PROFILE_RECORD.id,
        loaded: true,
        hasComplete: DEMO_SCANS.some((scan) => scan.status === "complete"),
        lastLabel: formatRelativeDay(DEMO_SCANS[0]?.started_at),
        lastSurfaced: DEMO_SCANS[0]?.surfaced_count ?? null,
      });
      return () => { cancelled = true; };
    }
    const profileId =
      activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId || !window.api?.scan?.list) {
      setScanMeta({
        profileId: profileId || null,
        loaded: true,
        hasComplete: false,
        lastLabel: null,
        lastSurfaced: null,
      });
      return;
    }
    setScanMeta({
      profileId,
      loaded: false,
      hasComplete: false,
      lastLabel: null,
      lastSurfaced: null,
    });
    window.api.scan.list(profileId)
      .then((data) => {
        if (cancelled) return;
        const all = Array.isArray(data?.scans) ? data.scans : [];
        // A `partial_ready` scan has already produced Stage 1 results, so it
        // counts as "has results" — otherwise a refresh mid-scan (e.g. the
        // scan-progress poll bumping scanRefreshKey) would flip the dashboard
        // back to the pre-scan / radar view and hide the streaming inbox.
        const done = all.filter(
          (s) => s.status === "complete" || s.status === "partial_ready"
        );
        const last = done[0];
        setScanMeta({
          profileId,
          loaded: true,
          hasComplete: done.length > 0,
          lastLabel: last ? formatRelativeDay(last.started_at) : null,
          lastSurfaced: last ? (last.surfaced_count ?? 0) : null,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setScanMeta({
          profileId,
          loaded: true,
          hasComplete: false,
          lastLabel: null,
          lastSurfaced: null,
        });
      });
    return () => { cancelled = true; };
  }, [activeProfileId, scanRefreshKey]);

  // Feed-level state (filters, etc.)
  const [feedState, setFeedState] = React.useState({
    density: t.density,
    scoreMode: t.scoreMode,
    sort: t.sort,
    threshold: t.threshold,
    highOnly: t.highOnly,
    query: "",
    searchScope: "all",
    subredditFilter: "all",
    hideNotRelevant: false,
  });

  // Sync tweaks into feed state
  React.useEffect(() => {
    setFeedState((s) => ({
      ...s,
      density: t.density,
      scoreMode: t.scoreMode,
      sort: t.sort,
      threshold: t.threshold,
      highOnly: t.highOnly,
    }));
  }, [t.density, t.scoreMode, t.sort, t.threshold, t.highOnly]);

  const [theme, setTheme] = React.useState(loadTheme);
  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    saveTheme(theme);
  }, [theme]);
  const onToggleTheme = React.useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    []
  );

  // ────────────────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────────────────
  const resetProfileScopedState = React.useCallback(() => {
    oppsRequestSeqRef.current += 1;
    setOpps([]);
    setOppsLoading(false);
    clearDetailState();
  }, [clearDetailState]);

  const switchActiveProfileContext = React.useCallback((id, nextScreen = "dashboard") => {
    activeProfileIdRef.current = id || "";
    setActiveProfileId(id || "");
    try {
      if (id) window.api?.storage?.writeProfileId?.(id);
      else window.api?.storage?.clearProfileId?.();
    } catch (e) {}
    resetProfileScopedState();
    goToScreen(nextScreen);
  }, [goToScreen, resetProfileScopedState]);

  // Feedback flow:
  //   1. Immediately reflect the user's choice in local state (optimistic).
  //   2. POST to the backend so opportunity_feedback persists and
  //      opportunities.status updates for the inbox query.
  //   3. Show a calm toast that names the algorithmic effect, matching
  //      the spec's "Your feedback tunes future scans for this profile"
  //      promise.
  // Errors don't roll the UI back — the toast just dims so a transient
  // backend hiccup doesn't make the user feel like they fat-fingered.
  const onFeedback = (id, value) => {
    let contentItemId = null;
    setOpps((arr) => arr.map((o) => {
      if (o.id !== id) return o;
      seenOppIdsRef.current.add(id);
      contentItemId = o.contentItemId;
      return { ...o, feedback: value, isNew: false };
    }));

    const profileId = activeProfileIdRef.current;
    if (profileId && contentItemId && window.api?.opportunities?.feedback) {
      window.api.opportunities
        .feedback(profileId, contentItemId, value)
        .catch((e) => {
          console.warn("Feedback save failed:", e?.message || e);
        });
    }

    if (value === "relevant") pushToast("Marked as relevant. Future scans will favor similar threads.");
    else if (value === "not_relevant") pushToast("Marked as not relevant. We'll suppress similar matches.");
    else if (value === "wrong_customer") pushToast("Thanks — we'll de-prioritize this persona.");
    else if (value === "too_broad") pushToast("Thanks — we'll narrow the trigger phrases.");
    else if (value === "already_seen") pushToast("Thanks — hiding duplicates.");
  };

  const onDeleteOpportunity = React.useCallback(async (id) => {
    if (!id) return;

    setOpps((arr) => arr.filter((o) => o.id !== id));
    seenOppIdsRef.current.delete(id);
    setOppsUpdatedAt(Date.now());
    if (selectedId === id) closeDetail();

    if (isDemo) {
      pushToast("Opportunity removed from the demo inbox.");
      return;
    }

    const profileId = activeProfileIdRef.current;
    if (!profileId || !window.api?.opportunities?.delete) {
      pushToast("Couldn't delete opportunity.", "error");
      refreshOpps(profileId);
      return;
    }

    try {
      await window.api.opportunities.delete(profileId, id);
      pushToast("Opportunity deleted.");
    } catch (e) {
      console.warn("Opportunity delete failed:", e?.message || e);
      pushToast("Couldn't delete opportunity.", "error");
      refreshOpps(profileId);
    }
  }, [closeDetail, isDemo, pushToast, refreshOpps, selectedId]);

  const onDeleteScan = React.useCallback(async (scan) => {
    const scanId = scan?.id || scan;
    if (!scanId || !window.api?.scan?.delete) return;
    await window.api.scan.delete(scanId);
    seenOppIdsRef.current = new Set();
    await refreshOpps();
    setScanRefreshKey((k) => k + 1);
    backToScansRoute({ replace: true });
    pushToast("Scan deleted.");
  }, [backToScansRoute, pushToast, refreshOpps]);

  const onStopScan = React.useCallback(async (scan) => {
    const scanId = scan?.id || scan;
    if (!scanId || !window.api?.scan?.stop) return;
    await window.api.scan.stop(scanId);
    setIsScanning(false);
    seenOppIdsRef.current = new Set();
    await refreshOpps();
    setScanRefreshKey((k) => k + 1);
    pushToast("Scan stopped.");
  }, [pushToast, refreshOpps]);

  // "Scan now" buttons just open the dialog; runScanWithParams does the work
  // once the user confirms their choices.
  const onScanNow = () => {
    if (isScanning) return;
    const profileId = window.api?.storage?.readProfileId?.();
    if (!profileId) {
      pushToast("Finish your profile setup before running a scan.", "error");
      return;
    }
    setScanDialogOpen(true);
  };

  // Timeout ceiling for waiting on Stage 2. 8 minutes is generous — most
  // Stage 2 runs finish in 60–120 s even on a 7-day window across many subs.
  const STAGE2_POLL_TIMEOUT_MS = 8 * 60 * 1000;

  // Wait for the backend to flip a scan from 'partial_ready'/'running' to a
  // terminal state ('complete' or 'failed'). Uses Supabase Realtime when
  // available (push, zero HTTP chatter) and falls back to a single final
  // list fetch if the subscription cannot be established.
  const pollScanUntilComplete = React.useCallback(
    async (profileId, scanId, onProgress) => {
      const started = Date.now();
      const supabase = window.getSupabase && window.getSupabase();

      // Fast path: Supabase Realtime subscription on the specific scan row.
      if (supabase) {
        return new Promise((resolve) => {
          let settled = false;
          const timeout = setTimeout(() => {
            if (!settled) {
              settled = true;
              channel.unsubscribe();
              resolve(null);
            }
          }, STAGE2_POLL_TIMEOUT_MS);

          const channel = supabase
            .channel(`scan-status-${scanId}`)
            .on(
              "postgres_changes",
              {
                event: "UPDATE",
                schema: "public",
                table: "opportunity_scans",
                filter: `id=eq.${scanId}`,
              },
              (payload) => {
                const row = payload.new || {};
                const terminal =
                  row.status &&
                  row.status !== "partial_ready" &&
                  row.status !== "running";
                if (terminal) {
                  if (!settled) {
                    settled = true;
                    clearTimeout(timeout);
                    channel.unsubscribe();
                    resolve(row);
                  }
                } else if (typeof onProgress === "function") {
                  // Intermediate count bump — a follow-up chunk surfaced more
                  // while the scan is still partial_ready. Wake the inbox
                  // without resolving so cards stream in as they're found.
                  try { onProgress(row); } catch (e) {}
                }
              }
            )
            .subscribe((status) => {
              if (status === "SUBSCRIBED") {
                // Subscription live — nothing else to do until an UPDATE arrives.
              } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
                // Fall back to a single list fetch so we don't hang forever.
                if (!settled) {
                  settled = true;
                  clearTimeout(timeout);
                  channel.unsubscribe();
                  window.api.scan
                    .list(profileId)
                    .then((data) => {
                      const scans = Array.isArray(data?.scans) ? data.scans : [];
                      const me = scans.find((s) => s.id === scanId);
                      resolve(
                        me &&
                          me.status !== "partial_ready" &&
                          me.status !== "running"
                          ? me
                          : null
                      );
                    })
                    .catch(() => resolve(null));
                }
              }
            });
        });
      }

      // Fallback (no Supabase client): do one final list fetch after a short
      // delay. In practice the Realtime path is always available.
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const data = await window.api.scan.list(profileId);
        const scans = Array.isArray(data?.scans) ? data.scans : [];
        const me = scans.find((s) => s.id === scanId);
        if (me && me.status !== "partial_ready" && me.status !== "running") {
          return me;
        }
      } catch (e) {
        console.warn("final scan status fetch failed:", e?.message || e);
      }
      return null;
    },
    []
  );

  // Tracks the scan id we're currently "following" (streaming progress for) so
  // a fresh scan and the reload-recovery effect can't double-attach to the
  // same scan.
  const followedScanIdRef = React.useRef(null);

  // Follow an in-flight scan to completion: show the sticky progress toast,
  // stream new opportunities in as the backend bumps surfaced_count (Realtime
  // push + the isScanning poll fallback), and show a final toast when it
  // finishes. Shared by a fresh scan (runScanWithParams) AND the reload-
  // recovery effect below, so reloading mid-scan re-attaches the live view
  // instead of going silent until a manual refresh.
  const followLiveScan = React.useCallback(
    async (profileId, scanId, opts = {}) => {
      if (!profileId || !scanId) return;
      if (followedScanIdRef.current === scanId) return;
      followedScanIdRef.current = scanId;
      setIsScanning(true);

      const PROGRESS_TOAST = "scan-progress";
      const followup = opts.followup || null;
      const isCommentsPass = followup === "comments";
      const baseSurfaced = Number(opts.stage1Surfaced || 0);
      let liveSurfaced = baseSurfaced;

      const stillScanningCopy = () =>
        liveSurfaced > 0
          ? isCommentsPass
            ? `${liveSurfaced} found · still scanning comment threads…`
            : `${liveSurfaced} found · still scanning…`
          : isCommentsPass
            ? "Scanning comment threads…"
            : "Still scanning for opportunities…";

      pushToast(opts.initialToast || stillScanningCopy(), "pending", {
        id: PROGRESS_TOAST,
        sticky: true,
      });

      const onScanProgress = (row) => {
        const surfaced =
          typeof row?.surfaced_count === "number"
            ? row.surfaced_count
            : liveSurfaced;
        if (surfaced > liveSurfaced) liveSurfaced = surfaced;
        setScanRefreshKey((k) => k + 1);
        refreshOpps(undefined, { silent: true });
        pushToast(stillScanningCopy(), "pending", {
          id: PROGRESS_TOAST,
          sticky: true,
        });
      };

      try {
        const finalScan = await pollScanUntilComplete(
          profileId, scanId, onScanProgress
        );
        setScanRefreshKey((k) => k + 1);
        await refreshOpps();
        dismissToast(PROGRESS_TOAST);
        if (!finalScan) {
          pushToast(
            isCommentsPass
              ? "Still searching comment threads — refresh the Scans tab in a minute."
              : "Scan is taking a while — refresh the Scans tab in a minute.",
            "info"
          );
        } else if (finalScan.status === "failed") {
          pushToast(
            finalScan.error_message ||
              "Scan failed. Your saved results are intact.",
            "error"
          );
        } else {
          const finalSurfaced = finalScan.surfaced_count ?? liveSurfaced;
          const appended = Math.max(0, finalSurfaced - baseSurfaced);
          if (appended > 0) {
            pushToast(
              isCommentsPass
                ? `Found ${appended} more in comment threads (${finalSurfaced} total).`
                : `Scan complete. Found ${appended} more ${appended === 1 ? "opportunity" : "opportunities"} (${finalSurfaced} total).`
            );
          } else {
            pushToast(
              finalSurfaced > 0
                ? `Scan complete. ${finalSurfaced} ${finalSurfaced === 1 ? "opportunity" : "opportunities"} surfaced.`
                : "Scan complete. No new opportunities this time."
            );
          }
        }
      } finally {
        setIsScanning(false);
        followedScanIdRef.current = null;
      }
    },
    [pushToast, dismissToast, refreshOpps, pollScanUntilComplete]
  );

  // Reload recovery. The live streaming (Realtime sub + toast + isScanning
  // poll) lives in the session that started the scan, so a page reload mid-scan
  // would go silent even though the backend scan keeps running. On mount /
  // profile change, look for an in-flight scan (running / partial_ready) and
  // re-attach the live view so results keep flowing without a manual refresh.
  React.useEffect(() => {
    if (isDemo) return undefined;
    const profileId = activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId || !window.api?.scan?.list) return undefined;
    let cancelled = false;
    window.api.scan
      .list(profileId)
      .then((data) => {
        if (cancelled) return;
        const all = Array.isArray(data?.scans) ? data.scans : [];
        const live = all.find(
          (s) => s.status === "running" || s.status === "partial_ready"
        );
        if (live && followedScanIdRef.current !== live.id) {
          followLiveScan(profileId, live.id, {
            stage1Surfaced: live.surfaced_count || 0,
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isDemo, activeProfileId, followLiveScan]);

  const runScanWithParams = async (params) => {
    setScanDialogOpen(false);
    if (isScanning) return;
    const profileId = window.api?.storage?.readProfileId?.();
    if (!profileId) {
      pushToast("Finish your profile setup before running a scan.", "error");
      return;
    }
    setIsScanning(true);

    // One sticky toast (fixed id, replaced by later pushes) that narrates the
    // scan's phase: initial pull → Stage 1 results → Stage 2 deep scan → done.
    const PROGRESS_TOAST = "scan-progress";
    pushToast(
      "Looking for opportunities…",
      "pending",
      { id: PROGRESS_TOAST, sticky: true }
    );

    // Bump the scans list almost immediately so the "running" row appears
    // as soon as the backend has registered the scan (usually < 1s).
    setTimeout(() => setScanRefreshKey((k) => k + 1), 2000);

    // The dialog hands back r/-prefixed labels; the backend wants bare names.
    const bareSubs = (params?.subreddits || [])
      .map((s) => String(s).replace(/^\/?r\//i, "").trim())
      .filter(Boolean);

    // Remember exactly which subreddits this scan targeted so the live
    // scanning view lists only the selected ones.
    setScanSubreddits(bareSubs);

    // Single request now: the backend decides progressive (Stage 1 sync +
    // Stage 2 in a background thread) vs single-stage (delta ingest + match)
    // and does its own subreddit ingest. The frontend only orchestrates the
    // post-Stage-1 polling when `status === 'partial_ready'`.
    let result;
    try {
      result = await window.api.scan.run(profileId, {
        window: params?.window,
        subreddits: bareSubs,
        themes: params?.themes,
        content_type: params?.contentType,
        match_floor: params?.threshold,
      });
    } catch (e) {
      dismissToast(PROGRESS_TOAST);
      setScanRefreshKey((k) => k + 1);
      setIsScanning(false);
      pushToast(e?.message || "Scan failed. Check the server logs.", "error");
      return;
    }

    setScanRefreshKey((k) => k + 1);
    // The backend accepted the scan and returned a successful response, so
    // don't let the "first scan" overlay linger while the scan-history fetch
    // catches up on its own round-trip.
    setScanMeta((prev) => {
      if ((prev.profileId || null) !== profileId) return prev;
      return {
        ...prev,
        loaded: true,
        hasComplete: true,
        lastSurfaced:
          typeof result?.surfaced_count === "number"
            ? result.surfaced_count
            : prev.lastSurfaced,
      };
    });
    // Reset seen ids on a fresh scan so the newly-judged opportunities light
    // up as new — the user explicitly asked for this refresh.
    seenOppIdsRef.current = new Set();
    await refreshOpps();

    const stage1Surfaced = result?.surfaced_count ?? 0;
    const isProgressive = result?.status === "partial_ready";

    if (!isProgressive) {
      dismissToast(PROGRESS_TOAST);
      pushToast(
        stage1Surfaced > 0
          ? `Scan complete. ${stage1Surfaced} ${stage1Surfaced === 1 ? "opportunity" : "opportunities"} surfaced.`
          : "Scan complete. No new opportunities this time."
      );
      setIsScanning(false);
      return;
    }

    // Stage 1 returned with results visible. Tell the user, then wait on
    // the follow-up pass (deep window scan, or — for 24h — the comments
    // pass that only fetches comment trees for posts whose embeddings
    // matched). isScanning stays true so "Scan now" stays disabled
    // until the scan is really done.
    const followup = result?.followup || "deep";
    const isCommentsPass = followup === "comments";
    const pendingCopy = isCommentsPass
      ? stage1Surfaced > 0
        ? `${stage1Surfaced} ${stage1Surfaced === 1 ? "post" : "posts"} surfaced. Scanning comment threads…`
        : "Posts scanned. Now searching comment threads…"
      : stage1Surfaced > 0
        ? `${stage1Surfaced} initial ${stage1Surfaced === 1 ? "opportunity" : "opportunities"} surfaced. Still scanning…`
        : "Initial pass done. Still scanning for more…";
    // Hand off to the shared follower: it streams new cards in as the backend
    // bumps surfaced_count and shows the final toast on completion. The same
    // path is used by the reload-recovery effect, so the live view survives a
    // page refresh mid-scan.
    await followLiveScan(profileId, result.scan_id, {
      stage1Surfaced,
      followup,
      initialToast: pendingCopy,
    });
  };

  const onSwitchProfile = (id) => {
    switchActiveProfileContext(id, "dashboard");
  };
  const onNewProfile = async () => {
    // Plan gating: Free plan caps total projects at 1. We refresh from the
    // server before throwing up the gate so a stale cached plan can't lock
    // a user out right after they upgrade.
    try {
      const resp = await window.api?.billing?.subscription?.();
      const sub = resp?.subscription;
      const cap = sub?.plan_definition?.limits?.max_projects;
      if (cap != null && businessProfiles.length >= cap) {
        pushToast(
          sub.plan === "free"
            ? `Free plan is limited to ${cap} project. Please upgrade.`
            : `You've hit your plan's project cap (${cap}).`,
          "error",
        );
        goToScreen("pricing");
        return;
      }
    } catch (e) {
      // Best-effort check — if /billing/subscription fails we let creation
      // proceed and the backend cap (none today) is the real fence.
    }
    resetProfileScopedState();
    setSuppressCreateProfilePrompt(false);
    setShowProfileCreator(true);
  };

  const closeProfileCreator = () => {
    setShowProfileCreator(false);
  };

  const dismissForcedProfileCreator = React.useCallback(() => {
    setSuppressCreateProfilePrompt(true);
    setShowProfileCreator(false);
  }, []);

  const handleCreatedProfile = async (createdProfileId, plan) => {
    if (!createdProfileId) return false;
    try {
      if (plan) {
        await window.api?.profile?.saveSearchPlan?.(createdProfileId, plan);
      }
      const rows = await refreshBusinessProfiles();
      const nextId = createdProfileId || rows?.[0]?.id;
      switchActiveProfileContext(nextId, "dashboard");
      setSearchPlan(null);
      setProfileOverrides({});
      setSuppressCreateProfilePrompt(false);
      setShowProfileCreator(false);
      await refreshOpps(nextId);
      pushToast("Business profile created successfully.");
      return true;
    } catch (e) {
      pushToast(e?.message || "Could not finalize business profile.", "error");
      return false;
    }
  };

  // If onboarding completion fires before the post-OAuth bootstrap finishes
  // (slow network), defer the transition until auth is set so the user
  // lands cleanly on the dashboard.
  const [scanAnimationDone, setScanAnimationDone] = React.useState(false);
  const onCompleteOnboard = async () => {
    if (!auth) { setScanAnimationDone(true); return; }
    if (window.api?.storage?.readPendingProfile?.()) {
      setFinalizingOnboarding(true);
      try {
        const created = await window.api?.profile?.flushPending?.();
        const createdProfileId = created?.business_profile_id || null;
        const createdProfile = created?.profile || null;
        const userId = window.api?.auth?.readUserId?.() || null;
        if (!createdProfileId) {
          throw new Error("Pending onboarding did not create a business profile.");
        }
        try { window.api?.storage?.writeProfileId?.(createdProfileId); } catch (e) {}
        setActiveProfileId(createdProfileId);
        if (createdProfile) {
          setBusinessProfiles([createdProfile]);
          setBusinessProfilesHydrated(true);
          setBusinessProfilesError(null);
          setProfileDetailsById((prev) => ({
            ...prev,
            [createdProfileId]: createdProfile,
          }));
        }
        await refreshBusinessProfiles(userId);
        setPendingPostLoginAction(null);
        savePostLoginAction(null);
        setSearchPlan(null);
      } catch (e) {
        console.warn("Could not finalize onboarding from in-app approval:", e);
        setFinalizingOnboarding(false);
        return;
      }
      setFinalizingOnboarding(false);
    }
    setShowOnboard(false);
    setOnboardStep("done");
    goToScreen("dashboard", { replace: true });
  };
  React.useEffect(() => {
    if (!auth || !scanAnimationDone) return;
    setShowOnboard(false);
    setOnboardStep("done");
    goToScreen("dashboard", { replace: true });
    setScanAnimationDone(false);
  }, [auth, goToScreen, scanAnimationDone]);

  const onSkipToDemo = () => {
    // Demo path should never trigger the "first-scan" post-login flow, even
    // if a pending action was queued earlier — clear it so onLogin's branch
    // resolves to "go to dashboard."
    setPendingPostLoginAction(null);
    savePostLoginAction(null);
    onLogin({ email: "demo@example.com" });
  };

  const exitDemoToOnboarding = React.useCallback(() => {
    if (!isDemo) return;
    saveAuth(null);
    setAuth(null);
    setCurrentUser(null);
    setCurrentUserLoading(false);
    setBusinessProfiles([]);
    setBusinessProfilesLoading(false);
    setActiveProfileId("");
    activeProfileIdRef.current = "";
    try {
      window.api?.storage?.clearProfileId?.();
      window.api?.storage?.clearPendingProfile?.();
      window.api?.storage?.clearPendingPlan?.();
    } catch (e) {}
    setPendingPostLoginAction(null);
    savePostLoginAction(null);
    setSearchPlan(null);
    setProfileOverrides({});
    resetProfileScopedState();
    setShowProfileCreator(false);
    setShowLogin(false);
    setShowOnboard(true);
    setOnboardStep("setup");
    setScanAnimationDone(false);
    setScreen("dashboard");
    if (location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [isDemo, location.pathname, navigate, resetProfileScopedState]);

  const onApproveFirstScan = () => {
    if (!auth) {
      setPendingPostLoginAction(POST_LOGIN_FINISH_ONBOARDING);
      setShowLogin(true);
      return;
    }
    resetProfileScopedState();
    onCompleteOnboard();
  };

  const onUpdateProfile = (form, options = {}) => {
    const {
      toast = "Profile updated. Changes apply on next scan.",
      silent = false,
    } = options;
    // profile-setup.jsx already persists via the API on submit. Update the
    // locally-displayed profile so the view reflects the edit immediately.
    setProfileOverrides((prev) => ({
      ...prev,
      [baseProfile.id]: {
        name: form.name,
        website: form.website,
        description: form.description,
        customers: form.customers,
        useCases: form.useCases,
        painPoints: form.painPoints,
        competitors: form.competitors,
        keywords: form.keywords,
        avoid: form.avoid,
        subreddits: form.subreddits,
      },
    }));
    refreshBusinessProfiles();
    if (!silent && toast) pushToast(toast);
  };

  // Resolve the currently-selected opp. Three paths because cluster opps
  // are synthetic (not in `opps`) and externalOpp is lost on page reload:
  //   1. Direct lookup in the current opps list (covers solo opps).
  //   2. externalOpp held over from the click that opened the panel
  //      (covers opps from past scans or fresh clicks on a cluster card).
  //   3. URL-restored cluster: id is "cluster:<postId>" but state was lost on
  //      reload — rebuild the cluster from current opps so the panel still
  //      hydrates if the user shares/refreshes a cluster URL.
  const selectedOpp = (() => {
    if (!selectedId) return null;
    const direct = opps.find((o) => o.id === selectedId);
    if (direct) return direct;
    if (externalOpp && externalOpp.id === selectedId) return externalOpp;
    if (selectedId.startsWith("cluster:") && window.clusterOpps) {
      const postId = selectedId.slice("cluster:".length);
      const members = opps.filter(
        (o) => o.kind === "comment" && o.redditPostId === postId
      );
      if (members.length > 0) {
        const rebuilt = window.clusterOpps(members);
        const found = rebuilt.find((c) => c.id === selectedId);
        if (found) return found;
      }
    }
    return null;
  })();
  const newCount = opps.filter((o) => o.isNew && o.feedback !== "not_relevant").length;
  const selectedBusinessProfile =
    uiBusinessProfiles.find((p) => p.id === activeProfileId) ||
    uiBusinessProfiles[0] ||
    null;
  const topbarProfile = selectedBusinessProfile || (isDemo ? {
    id: "demo-workspace",
    name: "Demo workspace",
    initials: "D",
    color: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    website: "Preview data",
    lastScan: "—",
  } : {
    id: "create-profile",
    name: "Create business profile",
    initials: "+",
    color: "linear-gradient(135deg, #475569, #334155)",
    website: "No business profiles yet",
    lastScan: "—",
  });
  const currentUserValue = React.useMemo(() => ({
    user: currentUser,
    loading: currentUserLoading,
    refreshUser: refreshCurrentUser,
    setUser: setCurrentUser,
  }), [currentUser, currentUserLoading, refreshCurrentUser]);

  // ────────────────────────────────────────────────────────
  // Not signed in → welcome → onboarding → login prompt
  // ────────────────────────────────────────────────────────
  let content = null;
  if (!auth) {
    if (authBootstrapLoading || authTransitionMode === "logout") {
      const isLogoutTransition = authTransitionMode === "logout";
      content = (
        <div
          className="auth-wrap screen-enter"
          style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}
        >
          <style>{`@keyframes authTransitionSpin { to { transform: rotate(360deg); } }`}</style>
          <div
            className="auth-card"
            style={{ maxWidth: 340, padding: "32px 26px", textAlign: "center", alignItems: "center", gap: 14 }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                border: "3px solid color-mix(in srgb, var(--accent) 18%, transparent)",
                borderTopColor: "var(--accent)",
                animation: "authTransitionSpin 700ms linear infinite",
                boxShadow: "0 0 0 6px color-mix(in srgb, var(--accent) 10%, transparent)",
              }}
            />
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
              {isLogoutTransition ? "Logging you out..." : "One moment..."}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
              {isLogoutTransition ? "Closing your session securely." : "Loading your workspace."}
            </div>
          </div>
        </div>
      );
    } else if (showLogin) {
      content = (
        <>
          <window.LoginScreen
            onLogin={onLogin}
            onBack={() => {
              setShowLogin(false);
              clearOnboardingDraft();
              setShowOnboard(false);
              setOnboardStep("done");
              navigate("/", { replace: true });
            }}
          />
          <TweaksHost t={t} setT={setT} onResetOnboarding={() => { setShowOnboard(true); setOnboardStep("welcome"); }} />
        </>
      );
    } else if (showOnboard) {
      content = (
        <>
          <window.OnboardingShell
            step={onboardStep}
            onStepChange={setOnboardStep}
            seed={onboardSeed}
            onSeedChange={setOnboardSeed}
            onClearSignupIntent={clearSignupIntent}
            onApprove={onApproveFirstScan}
            onSkipDemo={onSkipToDemo}
            onLogin={() => setShowLogin(true)}
            onExitHome={() => {
              clearOnboardingDraft();
              setShowOnboard(false);
              setOnboardStep("done");
              navigate("/", { replace: true });
            }}
            searchPlan={searchPlan}
            onPlanGenerated={onPlanGenerated}
            theme={theme}
            onToggleTheme={onToggleTheme}
          />
          <TweaksHost t={t} setT={setT} onResetOnboarding={() => { setShowOnboard(true); setOnboardStep("welcome"); }} />
        </>
      );
    } else {
      content = (
        <>
          <window.WelcomeScreen
            onStart={() => { setShowOnboard(true); setOnboardStep("setup"); }}
            onBuildRadar={startOnboarding}
            onExample={onSkipToDemo}
            onLogin={() => setShowLogin(true)}
            theme={theme}
            onToggleTheme={onToggleTheme}
          />
          <TweaksHost t={t} setT={setT} onResetOnboarding={() => { setShowOnboard(true); setOnboardStep("welcome"); }} />
        </>
      );
    }
  } else if (finalizingOnboarding) {
    content = (
      <div
        className="auth-wrap screen-enter"
        style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}
      >
        <style>{`@keyframes authTransitionSpin { to { transform: rotate(360deg); } }`}</style>
        <div
          className="auth-card"
          style={{ maxWidth: 360, padding: "32px 26px", textAlign: "center", alignItems: "center", gap: 14 }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              border: "3px solid color-mix(in srgb, var(--accent) 18%, transparent)",
              borderTopColor: "var(--accent)",
              animation: "authTransitionSpin 700ms linear infinite",
              boxShadow: "0 0 0 6px color-mix(in srgb, var(--accent) 10%, transparent)",
            }}
          />
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>
            Just a moment...
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
            Creating your workspace and saving your radar.
          </div>
        </div>
      </div>
    );
  } else if (showOnboard) {
    // ────────────────────────────────────────────────────────
    // Render onboarding shell if active
    // ────────────────────────────────────────────────────────
    content = (
      <>
        <window.OnboardingShell
          step={onboardStep}
          onStepChange={setOnboardStep}
          onApprove={onApproveFirstScan}
          onSkipDemo={onSkipToDemo}
          onExitHome={() => {
            clearOnboardingDraft();
            setShowOnboard(false);
            setOnboardStep("done");
            navigate("/", { replace: true });
          }}
          searchPlan={searchPlan}
          onPlanGenerated={onPlanGenerated}
          theme={theme}
          onToggleTheme={onToggleTheme}
        />
        <TweaksHost t={t} setT={setT} onResetOnboarding={() => { setShowOnboard(true); setOnboardStep("welcome"); }} />
      </>
    );
  } else {
    // ────────────────────────────────────────────────────────
    // Main app
    // ────────────────────────────────────────────────────────
    const hasScanMetaForActiveProfile =
      scanMeta.loaded &&
      scanMeta.profileId === (activeProfileId || null);
    const isConfirmedFirstScan =
      hasScanMetaForActiveProfile &&
      !oppsLoading &&
      !scanMeta.hasComplete &&
      !latestScan &&
      opps.length === 0;

    const state = {
      opps,
      oppsLoading,
      oppsUpdatedAt,
      latestScan,
      onRefreshOpps: refreshOpps,
      profile,
      profilesLoading: businessProfilesLoading,
      density: feedState.density,
      scoreMode: feedState.scoreMode,
      sort: feedState.sort,
      threshold: feedState.threshold,
      highOnly: feedState.highOnly,
      query: feedState.query,
      searchScope: feedState.searchScope,
      subredditFilter: feedState.subredditFilter,
      hideNotRelevant: feedState.hideNotRelevant,
      isScanning,
      scanSubreddits,
      firstScan: isConfirmedFirstScan,
    };

    const renderScreen = () => {
      switch (screen) {
        case "dashboard":
          return <window.DashboardScreen state={state} onNavigate={goToScreen} onOpenOpp={openOpp} onFeedback={onFeedback} onScanNow={onScanNow} />;
        case "opportunities":
          return <window.OpportunitiesScreen state={state} setState={setFeedState} onOpenOpp={openOpp} onFeedback={onFeedback} />;
        case "conversations":
          return <window.ConversationsScreen onNavigate={goToScreen} pushToast={pushToast} />;
        case "scans":
          return (
            <window.ScansScreen
              onScanNow={onScanNow}
              isScanning={isScanning}
              scanRefreshKey={scanRefreshKey}
              onOpenOpp={openOpp}
              onDeleteScan={isDemo ? null : onDeleteScan}
              onStopScan={isDemo ? null : onStopScan}
              onNavigate={goToScreen}
              routeScanId={routeScanId || backgroundScanId}
              onOpenScan={openScanRoute}
              onBackToScans={backToScansRoute}
              demoScans={isDemo ? DEMO_SCANS : null}
              demoScanResultsById={isDemo ? DEMO_SCAN_RESULTS_BY_ID : null}
            />
          );
        case "profile":
          return (
            <window.ProfileScreen
              profile={profile}
              onUpdateProfile={onUpdateProfile}
              demoProfile={isDemo ? DEMO_PROFILE_RECORD : null}
              hydratedProfile={profile?.id ? (profileDetailsById[profile.id] || null) : null}
              onRefreshProfile={refreshProfileDetail}
            />
          );
        case "settings":
          return (
            <window.SettingsScreen
              activeProfileId={activeProfileId}
              businessProfiles={businessProfiles}
              onSwitchProfile={onSwitchProfile}
              onRefreshProfiles={refreshBusinessProfiles}
              onNoProfilesRemaining={() => {
                setSuppressCreateProfilePrompt(true);
                setShowProfileCreator(false);
              }}
              onRefreshOpps={refreshOpps}
              onResultsCleared={() => {
                seenOppIdsRef.current = new Set();
                setScanRefreshKey((k) => k + 1);
                pushToast("Cleared all stored results for this profile.");
              }}
              onNavigate={goToScreen}
              onLogout={onLogout}
            />
          );
        case "pricing":
          return <window.PricingScreen onClose={() => goToScreen("dashboard")} />;
        case "mobile":
          return <window.MobileScreen state={state} onOpenOpp={openOpp} onFeedback={onFeedback} />;
        default:
          return null;
      }
    };

    content = isMobile ? (
      <div className="app app-mobile">
        <window.MobileApp
          screen={screen}
          onNavigate={(s) => { closeDetail({ nextScreen: s, replace: true }); goToScreen(s); }}
          state={state}
          setFeedState={setFeedState}
          selectedOpp={selectedOpp}
          detailOpen={detailOpen}
          onOpenOpp={openOpp}
          onCloseDetail={closeDetail}
          onFeedback={onFeedback}
          onDeleteOpportunity={onDeleteOpportunity}
          onScanNow={onScanNow}
          isScanning={isScanning}
          scanRefreshKey={scanRefreshKey}
          onLogout={onLogout}
          theme={theme}
          onToggleTheme={onToggleTheme}
          profile={profile}
          profiles={uiBusinessProfiles}
          profilesLoading={businessProfilesLoading}
          onSwitchProfile={onSwitchProfile}
          onNewProfile={onNewProfile}
          isDemo={isDemo}
          demoScans={isDemo ? DEMO_SCANS : null}
          demoScanResultsById={isDemo ? DEMO_SCAN_RESULTS_BY_ID : null}
          userEmail={auth?.email}
          newCount={newCount}
          activeProfileId={activeProfileId}
          onRefreshProfiles={refreshBusinessProfiles}
          onRefreshOpps={refreshOpps}
          onResultsCleared={() => {
            seenOppIdsRef.current = new Set();
            setScanRefreshKey((k) => k + 1);
            pushToast("Cleared all stored results for this profile.");
          }}
          onUpdateProfile={onUpdateProfile}
          onMakeYourOwn={exitDemoToOnboarding}
        />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
        <ProfileCreatorModal
          isOpen={showProfileCreator}
          onClose={closeProfileCreator}
          onCreated={handleCreatedProfile}
          dismissible={!mustCreateBusinessProfile}
          onForcedClose={mustCreateBusinessProfile ? dismissForcedProfileCreator : undefined}
        />
        <window.ScanDialog
          isOpen={scanDialogOpen}
          onClose={() => setScanDialogOpen(false)}
          onRun={runScanWithParams}
          profile={profile}
          firstScan={hasScanMetaForActiveProfile && !scanMeta.hasComplete}
          lastScanLabel={scanMeta.lastLabel}
          lastScanSurfaced={scanMeta.lastSurfaced}
        />
      </div>
    ) : (
      <div className="app">
        <window.Sidebar
          screen={screen}
          onNavigate={(s) => { closeDetail({ nextScreen: s, replace: true }); goToScreen(s); }}
          profile={profile}
          userEmail={auth?.email}
          onLogout={onLogout}
        />
        <div className="main-col">
        <window.Topbar
          profile={topbarProfile}
          profiles={uiBusinessProfiles}
          profilesLoading={businessProfilesLoading}
          onSwitchProfile={onSwitchProfile}
          onScanNow={onScanNow}
          onMakeYourOwn={exitDemoToOnboarding}
          isScanning={isScanning}
          latestScan={latestScan}
          onNavigate={goToScreen}
          onNewProfile={onNewProfile}
          isDemo={isDemo}
          theme={theme}
          onToggleTheme={onToggleTheme}
        />
          <div className="main-content scroll" key={screen}>
            {renderScreen()}
          </div>

          <window.DetailPanel
            opp={selectedOpp}
            isOpen={detailOpen}
            onClose={closeDetail}
            onFeedback={onFeedback}
            onDeleteOpportunity={onDeleteOpportunity}
            activeProfileId={activeProfileId}
            onNavigate={goToScreen}
            initialGenMode={pendingGenMode}
          />
        </div>
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
        <TweaksHost
          t={t}
          setT={setT}
          onResetOnboarding={() => { setShowOnboard(true); setOnboardStep("welcome"); goToScreen("dashboard", { replace: true }); }}
        />
        <ProfileCreatorModal
          isOpen={showProfileCreator}
          onClose={closeProfileCreator}
          onCreated={handleCreatedProfile}
          dismissible={!mustCreateBusinessProfile}
          onForcedClose={mustCreateBusinessProfile ? dismissForcedProfileCreator : undefined}
        />
        <window.ScanDialog
          isOpen={scanDialogOpen}
          onClose={() => setScanDialogOpen(false)}
          onRun={runScanWithParams}
          profile={profile}
          firstScan={hasScanMetaForActiveProfile && !scanMeta.hasComplete}
          lastScanLabel={scanMeta.lastLabel}
          lastScanSurfaced={scanMeta.lastSurfaced}
        />
      </div>
    );
  }

  return (
    <window.CurrentUserContext.Provider value={currentUserValue}>
      {content}
    </window.CurrentUserContext.Provider>
  );
}

function ProfileCreatorModal({ isOpen, onClose, onCreated, dismissible = true, onForcedClose }) {
  const [step, setStep] = React.useState("setup");
  const [draftForm, setDraftForm] = React.useState(null);
  const [generatedPlan, setGeneratedPlan] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");

  React.useEffect(() => {
    if (!isOpen) {
      setStep("setup");
      setDraftForm(null);
      setGeneratedPlan(null);
      setSubmitting(false);
      setSubmitError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const submitProfile = async (form, plan) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const resp = await window.api?.profile?.create?.(form);
      const createdProfileId = resp?.business_profile_id || null;
      if (!createdProfileId) {
        throw new Error("Could not create the business profile.");
      }
      const completed = await onCreated(createdProfileId, plan);
      if (!completed) {
        setSubmitting(false);
        setSubmitError("Could not finalize the business profile.");
      }
    } catch (e) {
      setSubmitting(false);
      setSubmitError(e?.message || "Could not create the business profile.");
    }
  };

  return (
    <>
      <div
        className="profile-creator-overlay"
        onClick={dismissible ? onClose : undefined}
      />
      <div className="profile-creator-shell" role="dialog" aria-modal="true">
        <div className="profile-creator-header">
          <div>
            <div className="profile-creator-eyebrow">Business profiles</div>
            <h2 className="profile-creator-title">
              {step === "setup" ? "Create a business profile" : "Review your radar"}
            </h2>
          </div>
          {dismissible ? (
            <button className="btn btn-ghost btn-sm" onClick={onClose}>
              <window.I.X size={14} />
              Cancel
            </button>
          ) : onForcedClose ? (
            <button className="btn btn-ghost btn-sm" onClick={onForcedClose} aria-label="Close" title="Close">
              <window.I.X size={14} />
            </button>
          ) : null}
        </div>

        <div className="profile-creator-body">
          {step === "setup" ? (
            <div className="profile-creator-flow profile-creator-flow-setup">
              <window.DescribeProductScreen
                onBack={dismissible ? onClose : undefined}
                onAnalyzed={(form, plan) => {
                  setDraftForm(form || null);
                  setGeneratedPlan(plan || null);
                  setSubmitError("");
                  setStep("map");
                }}
              />
            </div>
          ) : (
            <div className="profile-creator-flow profile-creator-flow-map">
              <window.RadarReviewScreen
                form={draftForm || {
                  name: "",
                  website: "",
                  description: "",
                  customers: "",
                  useCases: [],
                  painPoints: [],
                  competitors: [],
                  keywords: [],
                  avoid: [],
                  subreddits: [],
                }}
                searchPlan={generatedPlan || {
                  target_customers: [],
                  jobs_to_be_done: [],
                  pain_points: [],
                  trigger_phrases: [],
                  suggested_subreddits: [],
                  negative_matches: [],
                  search_themes: [],
                }}
                onRestart={() => {
                  setDraftForm(null);
                  setGeneratedPlan(null);
                  setSubmitError("");
                  setStep("setup");
                }}
                submitting={submitting}
                onApprove={submitProfile}
              />
              {submitError && (
                <div className="profile-creator-status profile-creator-status-error">
                  {submitError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ToastStack({ toasts, onDismiss }) {
  return (
    <div className="toast-stack">
      {toasts.map((tt) => (
        <div key={tt.id} className={"toast " + (tt.leaving ? "is-leaving" : "")}>
          {tt.kind === "pending" ? (
            <window.I.Sparkles
              size={13}
              style={{
                flex: "0 0 auto",
                color: "var(--accent)",
                animation: "spin 900ms linear infinite",
              }}
            />
          ) : (
            <span className="dot" style={{
              background: tt.kind === "error" ? "var(--danger)" : "var(--success)",
            }} />
          )}
          <span className="toast-text">{tt.text}</span>
          <button
            className="toast-close"
            type="button"
            onClick={() => onDismiss(tt.id)}
            aria-label="Dismiss notification"
            title="Dismiss"
          >
            <window.I.X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

function TweaksHost({ t, setT, onResetOnboarding }) {
  return (
    <window.TweaksPanel title="Tweaks">
      <window.TweakSection label="Feed display">
        <window.TweakRadio
          label="Density"
          value={t.density}
          options={[
            { value: "spacious", label: "Spacious" },
            { value: "medium", label: "Medium" },
            { value: "compact", label: "Compact" },
          ]}
          onChange={(v) => setT({ density: v })}
        />
        <window.TweakRadio
          label="Score display"
          value={t.scoreMode}
          options={[
            { value: "both", label: "% + tier" },
            { value: "tier", label: "Tier only" },
          ]}
          onChange={(v) => setT({ scoreMode: v })}
        />
      </window.TweakSection>

      <window.TweakSection label="Filtering">
        <window.TweakSelect
          label="Sort by"
          value={t.sort}
          options={[
            { value: "best", label: "Best match" },
            { value: "new", label: "Newest" },
            { value: "discussed", label: "Most discussed" },
          ]}
          onChange={(v) => setT({ sort: v })}
        />
        <window.TweakSelect
          label="Match threshold"
          value={String(t.threshold)}
          options={[
            { value: "60", label: "≥ 60% match" },
            { value: "75", label: "≥ 75% match" },
            { value: "85", label: "≥ 85% match" },
            { value: "90", label: "≥ 90% match" },
          ]}
          onChange={(v) => setT({ threshold: parseInt(v, 10) })}
        />
        <window.TweakToggle
          label="High-confidence only"
          value={t.highOnly}
          onChange={(v) => setT({ highOnly: v })}
        />
      </window.TweakSection>

      <window.TweakSection label="Onboarding">
        <window.TweakButton
          label="Restart onboarding"
          onClick={onResetOnboarding}
        />
      </window.TweakSection>
    </window.TweaksPanel>
  );
}

window.App = App;
